import { DispatchHistory } from "./EditSideProfile/DispatchHistory";
import useGradeConfig, {
  type GradeConfigQueryResult,
} from "@hooks/useGradeConfig";
import { resolveGradeLabel } from "@/utils/grade-util";
import { images } from "@img/imagesMap";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { DateUtils } from "@utils/dayUtill.ts";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import type { NurseResponse, UnionShift } from "@vTypes/nurse-type";
import type * as type from "@vTypes";
import { useNurseEditLogic } from "@hooks/useNurseEditLogic";
import { InfoItem } from "./NurseBasicInfo";
import NursePreceptorSection from "./NursePreceptorSection";
import { findNurseTeamName } from "@utils/team-util.ts";
import { sortShiftArray } from "@utils/nurse-util.ts";
import {
  useShiftsQuery,
  type ShiftReturnType,
} from "@hooks/roster-craete/useShiftsQuery";
import {
  useTeamsQuery,
  type TeamsSchema,
} from "@hooks/roster-craete/useTeamsQuery";
import useGroupMembers from "@hooks/useGroupMembers";
import type { GroupMembersResponse } from "@/api/nurses";
import { ShiftMapper, type Shift as MapperShift } from "@utils/shift-util.ts";
import NurseMyWeeklyOffSection from "./EditSideProfile/NurseMyWeeklyOffSection";
import NurseDateInfoSection from "./EditSideProfile/NurseDateInfoSection";
import NurseShiftInfoSection from "./EditSideProfile/NurseShiftInfoSection";
import NurseMonthlyLimitSection from "./EditSideProfile/NurseMonthlyLimitSection";
import ShiftTypeSelector from "./EditSideProfile/ShiftTypeSelector";
import { useWeeklyOffSettingsQuery } from "@/hooks/useWeeklyOffSettingsQuery";
import { cn } from "@/utils/cn";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useThrottle from "@/hooks/useThrottle";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CustomModal from "@/components/modal/CustomModal";
import httpClient from "@/config/axios";
import { useNurseAsOfMonthStore } from "@/store/nurseAsOfMonthStore";
import LeaveEligibilityFields from "@components/feature/head_nurse_mgmt/LeaveEligibilityFields";
import NightCycleStatus from "@/components/feature/head_nurse_mgmt/NightCycleStatus";
import GroupMultiSelect from "./EditSideProfile/GroupMultiSelect";
import { QUERY_BASE_KEY } from "@/constants/query-key";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { Input } from "@/components/ui/input";
import { sortTeamsByName } from "@/utils/team-sort-util";
import useRosterConfig, {
  type RosterConfigObject,
} from "@/hooks/useRosterConfig";
import {
  getActiveAssignmentsFromDetail,
  normalizeAssignmentReason,
  type NurseDetailResponse,
  type NurseInboundListItem,
  useNurseDetail,
} from "@/hooks/useNurseDetail";
import { useGroupsByOfficeQuery } from "@/hooks/useGroupsByOfficeQuery";
import { useLoginUser } from "@/hooks/useAuth";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { toast } from "@/components/toast/CustomToast";

interface NurseEditSideProfileProps {
  isOpen: boolean;
  onClose: () => void;
  nurseId: string | null;
  selectedGroupId: string | null;
  isAdmin: boolean;
  allNurses: NurseResponse[];
  onlyView?: boolean; // 읽기 전용 모드
  /** 타병동 근무자 등: 근무상태/원티드 섹션만 수정 제한 */
  disableShiftAndWantedEdit?: boolean;
  /**
   * 선택 월(시점). 둘 다 주어지면 팀 표시를 nurse_team_period(as_of_team) 기준으로 해석.
   * 없으면 캐시 멤버십(teamsSchema.team_members)으로 폴백 — 기존 동작 보존.
   */
  year?: number;
  month?: number;
  teamsSchema?: TeamsSchema;
  gradeData?: GradeConfigQueryResult;
  shiftsData?: ShiftReturnType;
  rosterConfig?: RosterConfigObject;
  monthMembers?: GroupMembersResponse;
}

type SupportedReason = "휴직" | "퇴사" | "파견" | "병동이동";
type ReasonActivity = {
  startDate: string;
  endDate: string;
  memo: string | null;
  targetGroupId: string;
};
type DispatchActivity = ReasonActivity & {
  assignmentId?: number;
};
type ReasonValidationErrors = {
  startDate: boolean;
  endDate: boolean;
  targetGroupId: boolean;
};

type AssignmentOperation = "create" | "update" | "cancel";
type AssignmentPatchPayload = {
  operation: AssignmentOperation;
  assignment_id?: number;
  reason?: SupportedReason;
  source_group_id?: string;
  target_group_id?: string | null;
  office_id?: string;
  start_date?: string;
  expected_end_date?: string | null;
  status?: "active" | "cancelled";
  /** 배정 메모 (서버 `note`) */
  note?: string | null;
  /** 파견 타겟 주말OFF — 프로필 키가 아닐 때 assignment 경로 */
  target_weekly_off_enabled?: number | null;
  target_weekly_off_type?: string | null;
  target_weekly_off_weekday?: number | null;
  target_shift_types?: string[] | null;
  target_grade?: number | null;
  target_fixed_shift?: string | null;
  target_wanted_max_requests?: number | null;
};

const MANAGED_REASONS_SET = new Set<string>([
  "휴직",
  "퇴사",
  "파견",
  "병동이동",
]);

/** 활성 배정(inbound) 한 건 → `reason_activity` 슬롯 */
function reasonActivityFromInboundRow(
  row: NurseInboundListItem,
  formatDateToString: (d: Date | string | null | undefined) => string
): ReasonActivity {
  return {
    startDate: row.startDate ? formatDateToString(new Date(row.startDate)) : "",
    endDate: row.endDate ? formatDateToString(new Date(row.endDate)) : "",
    memo: row.note ?? null,
    targetGroupId: row.target_group_id ?? row.targetGroupId ?? "",
  };
}

/**
 * 현재 병동(`selectedGroupId`)이 목적지인 파견 배정 행 — 없으면 `is_inbound`일 때 첫 파견.
 */
function resolveDispatchTargetRow(
  nurseDetail: NurseDetailResponse,
  selectedGroupId: string | null
): NurseInboundListItem | null {
  const active = getActiveAssignmentsFromDetail(nurseDetail);
  const dispatchRows = active.filter(
    (a) => normalizeAssignmentReason(a.reason) === "파견"
  );
  if (dispatchRows.length === 0) return null;

  if (selectedGroupId) {
    const hit = dispatchRows.find(
      (r) => (r.target_group_id ?? r.targetGroupId ?? "") === selectedGroupId
    );
    if (hit) return hit;
  }

  if (nurseDetail.is_inbound) return dispatchRows[0];
  return null;
}

/**
 * 파견 목적지 기준 `target_*`를 화면·폼 시드용으로 루트 필드에 얹은 상세.
 * 저장 PATCH에는 그대로 일반 키(grade, team_id, …)만 보내면 서버가 target_*에 매핑.
 */
function applyInboundTargetForDisplay(
  detail: NurseDetailResponse,
  row: NurseInboundListItem | null
): NurseDetailResponse {
  if (!row) return detail;
  const t = row;
  return {
    ...detail,
    weekly_off_weekday:
      t.target_weekly_off_weekday != null ? t.target_weekly_off_weekday : null,
    is_weekend_off: t.target_weekly_off_enabled === 1,
    fixed_shift: t.target_fixed_shift ?? null,
    wanted_max_requests: t.target_wanted_max_requests ?? null,
    grade:
      t.target_grade !== undefined && t.target_grade !== null
        ? Number(t.target_grade)
        : 0,
    team_id: t.target_team_id != null ? String(t.target_team_id) : null,
    allowed_shifts: t.target_shift_types ? [...t.target_shift_types] : [],
  };
}

/** GET 전용 필드 — PATCH 바디에 실으면 안 됨 */
const READONLY_NURSE_PATCH_KEYS = [
  "inbound",
  "current_assignment",
  "is_inbound",
] as const;

function sanitizeNursePatchBody(body: Record<string, unknown>) {
  for (const k of READONLY_NURSE_PATCH_KEYS) {
    delete body[k];
  }
  /** 명세: 원 소속 병동은 assignment/source 메타로만 다루고 nurse.group_id를 PATCH로 바꾸지 않음 */
  delete body.group_id;
}

/**
 * assignment/assignments PATCH: 소속 병동(상세 `group_id`)이 현재 열 컨텍스트(`selectedGroupId`)와
 * 같을 때만 보낸다. 타 병동(파견 목적지 등)에서 열면 UI에서 막아도 저장 경로에 assignment가 붙을 수 있어
 * 서버가 소스 병동 권한 오류로 거절한다.
 */
function canIncludeAssignmentInNursePatch(
  onlyView: boolean,
  disableShiftAndWantedEdit: boolean,
  detail: NurseDetailResponse | null | undefined,
  selectedGroupId: string | null
): boolean {
  if (onlyView) return false;
  if (disableShiftAndWantedEdit) return false;
  if (!detail || !selectedGroupId) return false;
  return detail.group_id === selectedGroupId;
}

/** diff 객체에서 undefined 제거(JSON 직렬화 시 누락과 동일하게) */
function omitUndefinedValues<T extends Record<string, unknown>>(obj: T): T {
  const out = {} as Record<string, unknown>;
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}

function patchValuesEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => patchValuesEqual(v, sb[i]));
  }
  return false;
}

function patchRecordDiff(
  prev: Record<string, unknown>,
  next: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(next)) {
    if (patchValuesEqual(prev[key], next[key])) continue;
    out[key] = next[key];
  }
  return out;
}

type AssignmentTargetProfilePayload = Pick<
  AssignmentPatchPayload,
  | "target_fixed_shift"
  | "target_grade"
  | "target_shift_types"
  | "target_wanted_max_requests"
  | "target_weekly_off_enabled"
  | "target_weekly_off_weekday"
  | "target_weekly_off_type"
>;

function buildFullNurseProfilePatch(options: {
  formData: {
    role: string;
    level_: string;
    grade: number;
    team: string;
    experience: string;
    allowed_shifts: string[];
    joining_date: string;
    resignation_reason: string | null;
    nurse_memo: string;
    is_head_nurse: boolean;
    weekly_off_weekday: number | null;
    preceptor_id: string | null;
    exclusion_partner_id: string | null;
    is_weekend_off: boolean;
    work_shifts: string[];
    enable_aide: boolean;
    enable_nurse_pair_preference: boolean;
    wanted_max_requests: number | null;
    fixed_shift: string | null;
  };
  teamsSchema: Array<{ team_id: number; team_name: string }>;
  preceptorMode: "preceptor" | "preceptee" | null;
  payloadResignationDate: string;
  payloadResignationMemo: string | null;
  /** 파견 타겟 행이 있으면 주말OFF는 assignment.target_weekly_off_enabled로만 보냄 */
  omitIsWeekendOff?: boolean;
}): Record<string, unknown> {
  const {
    formData,
    teamsSchema,
    preceptorMode,
    payloadResignationDate,
    payloadResignationMemo,
    omitIsWeekendOff,
  } = options;

  const targetTeam = teamsSchema?.find(
    (t) => String(t.team_id) === formData.team
  );
  const targetTeamId = targetTeam ? targetTeam.team_id : null;
  const gradeNum = formData.grade === 0 ? null : formData.grade;

  const patch: Record<string, unknown> = {
    role: formData.role,
    level_: formData.level_,
    team_id: targetTeamId,
    experience: formData.experience === "" ? 1 : Number(formData.experience),
    allowed_shifts: formData.allowed_shifts,
    joining_date: formData.joining_date || null,
    resignation_date: payloadResignationDate || null,
    resignation_reason: formData.resignation_reason,
    resignation_reason_memo: payloadResignationMemo,
    nurse_memo: formData.nurse_memo,
    is_head_nurse: formData.is_head_nurse,
    weekly_off_weekday: formData.weekly_off_weekday,
    preceptor_id:
      preceptorMode === "preceptor"
        ? null
        : preceptorMode === null
          ? null
          : formData.preceptor_id || null,
    exclusion_partner_id: formData.exclusion_partner_id,
    is_weekend_off: formData.is_weekend_off,
    work_shifts: formData.work_shifts,
    enable_aide: formData.enable_aide,
    enable_nurse_pair_preference: formData.enable_nurse_pair_preference,
    grade: gradeNum,
  };
  if (formData.fixed_shift != null && formData.fixed_shift !== "") {
    patch.fixed_shift = formData.fixed_shift;
  }
  if (formData.wanted_max_requests != null) {
    patch.wanted_max_requests = formData.wanted_max_requests;
  }
  if (omitIsWeekendOff) {
    delete patch.is_weekend_off;
  }
  return patch;
}

function buildAssignmentTargetProfilePayload(formData: {
  allowed_shifts: string[];
  fixed_shift: string | null;
  grade: number;
  is_weekend_off: boolean;
  wanted_max_requests: number | null;
  weekly_off_weekday: number | null;
}): AssignmentTargetProfilePayload {
  return {
    target_fixed_shift:
      formData.fixed_shift != null && formData.fixed_shift !== ""
        ? formData.fixed_shift
        : null,
    target_grade: formData.grade === 0 ? null : formData.grade,
    target_shift_types: formData.allowed_shifts,
    target_wanted_max_requests: formData.wanted_max_requests,
    target_weekly_off_enabled: formData.is_weekend_off ? 1 : 0,
    target_weekly_off_weekday: formData.weekly_off_weekday,
    target_weekly_off_type: null,
  };
}

/** 단건 근무상태 사유 — 파견 제외 (초기 시드·저장 diff 공통) */
const SINGLE_MANAGED_REASONS: SupportedReason[] = ["휴직", "퇴사", "병동이동"];

type PrecepteeAssignmentPeriod = {
  startDate: string;
  expectedEndDate: string;
};

type PreceptorPeerAssignmentDraft = PrecepteeAssignmentPeriod & {
  assignmentId?: number;
  note?: string | null;
};

type PreceptorPeerUpdatePayload = {
  operation: "create" | "update" | "cancel";
  target_nurse_id: string;
  assignment_id?: number; // deprecated(백엔드 무시) — target_nurse_id 로 식별
  start_date?: string;
  expected_end_date?: string | null;
  note?: string;
};

/** 본인이 프리셉티일 때 프리셉터+기간 지정 payload (PATCH body.preceptee_period). */
type PrecepteeSelfPeriodPayload = {
  operation: "create" | "update" | "cancel";
  preceptor_id?: string | null;
  start_date?: string;
  expected_end_date?: string | null;
};

function formatAssignmentDate(
  value: string | null | undefined,
  formatDateToString: (d: Date | string | null | undefined) => string
): string {
  return value ? formatDateToString(new Date(value)) : "";
}

function comparableInboundFields(
  row: NurseInboundListItem,
  formatDateToString: (d: Date | string | null | undefined) => string
): {
  start: string;
  end: string;
  target: string;
  memo: string | null;
  target_weekly_off_enabled: number | null;
  target_weekly_off_type: string | null;
  target_weekly_off_weekday: number | null;
  target_shift_types: string[] | null;
  target_grade: number | null;
  target_fixed_shift: string | null;
  target_wanted_max_requests: number | null;
} {
  const start = row.startDate
    ? formatDateToString(new Date(row.startDate))
    : "";
  const end = row.endDate ? formatDateToString(new Date(row.endDate)) : "";
  return {
    start,
    end,
    target: row.target_group_id ?? row.targetGroupId ?? "",
    memo: row.note ?? null,
    target_weekly_off_enabled:
      row.target_weekly_off_enabled != null
        ? row.target_weekly_off_enabled
        : null,
    target_weekly_off_type: row.target_weekly_off_type ?? null,
    target_weekly_off_weekday: row.target_weekly_off_weekday ?? null,
    target_shift_types: row.target_shift_types ?? null,
    target_grade: row.target_grade ?? null,
    target_fixed_shift: row.target_fixed_shift ?? null,
    target_wanted_max_requests: row.target_wanted_max_requests ?? null,
  };
}

/**
 * 배정 PATCH: 현재 선택 탭과 무관하게 **폼 스냅샷 diff만** 반영.
 * - 파견: 목록에서 빠진 assignment_id는 cancel(행 삭제), 나머지는 create/update.
 * - 휴직·퇴사·병동이동: startDate가 비면 기존 assignment_id를 cancel.
 *   값이 있으면 스냅샷 대비 변경분만 create/update.
 */
function buildAssignmentPayloads(
  nurseDetailData: NurseDetailResponse,
  selectedGroupId: string | null,
  formDataForAssignment: {
    allowed_shifts: string[];
    fixed_shift: string | null;
    grade: number;
    is_weekend_off: boolean;
    wanted_max_requests: number | null;
    weekly_off_weekday: number | null;
  },
  initialAssignments: NurseInboundListItem[],
  dispatchActivities: DispatchActivity[],
  reasonActivity: Record<SupportedReason, ReasonActivity>,
  formatDateToString: (d: Date | string | null | undefined) => string
): AssignmentPatchPayload[] {
  const payloads: AssignmentPatchPayload[] = [];
  const targetProfile =
    buildAssignmentTargetProfilePayload(formDataForAssignment);

  const primaryDispatchId = resolveDispatchTargetRow(
    nurseDetailData,
    selectedGroupId
  )?.id;

  const initialDispatchIds = new Set(
    initialAssignments
      .filter((a) => normalizeAssignmentReason(a.reason) === "파견")
      .map((a) => a.id)
      .filter((id): id is number => typeof id === "number")
  );

  const validDispatch = dispatchActivities.filter(
    (item) =>
      item.startDate !== "" && item.endDate !== "" && item.targetGroupId !== ""
  );
  const currentDispatchIds = new Set(
    validDispatch
      .map((item) => item.assignmentId)
      .filter((id): id is number => typeof id === "number")
  );

  for (const id of initialDispatchIds) {
    if (!currentDispatchIds.has(id)) {
      payloads.push({
        operation: "cancel",
        assignment_id: id,
      });
    }
  }

  for (const item of validDispatch) {
    if (item.assignmentId != null) {
      const initialRow = initialAssignments.find(
        (a) => a.id === item.assignmentId
      );
      const before =
        initialRow != null
          ? comparableInboundFields(initialRow, formatDateToString)
          : null;

      const isPrimaryDispatch =
        typeof primaryDispatchId === "number" &&
        item.assignmentId === primaryDispatchId &&
        initialRow != null &&
        normalizeAssignmentReason(initialRow.reason) === "파견";

      const beforeForCompare =
        before && isPrimaryDispatch && before.target_weekly_off_enabled == null
          ? {
              ...before,
              target_weekly_off_enabled: nurseDetailData.is_weekend_off
                ? 1
                : 0,
            }
          : before;

      const after = {
        start: item.startDate,
        end: item.endDate,
        target: item.targetGroupId,
        memo: item.memo ?? null,
        target_weekly_off_enabled: isPrimaryDispatch
          ? targetProfile.target_weekly_off_enabled
          : (initialRow?.target_weekly_off_enabled ?? null),
        target_weekly_off_type: isPrimaryDispatch
          ? targetProfile.target_weekly_off_type
          : (initialRow?.target_weekly_off_type ?? null),
        target_weekly_off_weekday: isPrimaryDispatch
          ? targetProfile.target_weekly_off_weekday
          : (initialRow?.target_weekly_off_weekday ?? null),
        target_shift_types: isPrimaryDispatch
          ? targetProfile.target_shift_types
          : (initialRow?.target_shift_types ?? null),
        target_grade: isPrimaryDispatch
          ? targetProfile.target_grade
          : (initialRow?.target_grade ?? null),
        target_fixed_shift: isPrimaryDispatch
          ? targetProfile.target_fixed_shift
          : (initialRow?.target_fixed_shift ?? null),
        target_wanted_max_requests: isPrimaryDispatch
          ? targetProfile.target_wanted_max_requests
          : (initialRow?.target_wanted_max_requests ?? null),
      };

      const profileDiff =
        beforeForCompare && isPrimaryDispatch
          ? patchRecordDiff(beforeForCompare, after)
          : {};
      const hasDateMemoChange =
        !beforeForCompare ||
        beforeForCompare.start !== after.start ||
        beforeForCompare.end !== after.end ||
        beforeForCompare.target !== after.target ||
        beforeForCompare.memo !== after.memo;
      const hasProfileChange = Object.keys(profileDiff).length > 0;

      if (!hasDateMemoChange && !hasProfileChange) {
        continue;
      }

      const patch: AssignmentPatchPayload = {
        operation: "update",
        assignment_id: item.assignmentId,
      };
      if (!beforeForCompare || beforeForCompare.start !== after.start)
        patch.start_date = after.start;
      if (!beforeForCompare || beforeForCompare.end !== after.end)
        patch.expected_end_date = after.end || null;
      if (!beforeForCompare || beforeForCompare.target !== after.target)
        patch.target_group_id = after.target;
      if (!beforeForCompare || beforeForCompare.memo !== after.memo)
        patch.note = after.memo;
      if (isPrimaryDispatch) {
        Object.assign(patch, profileDiff);
      }
      payloads.push(patch);
    } else {
      payloads.push({
        operation: "create",
        reason: "파견",
        source_group_id: nurseDetailData.group_id,
        target_group_id: item.targetGroupId,
        office_id: nurseDetailData.office_id,
        start_date: item.startDate,
        expected_end_date: item.endDate || null,
      });
    }
  }

  for (const sr of SINGLE_MANAGED_REASONS) {
    const activity = reasonActivity[sr];
    const shouldInclude = activity != null && activity.startDate !== "";

    const existingAssignmentForReason = initialAssignments.find(
      (a) => normalizeAssignmentReason(a.reason) === sr
    );
    const existingAssignmentId =
      typeof existingAssignmentForReason?.id === "number"
        ? existingAssignmentForReason.id
        : undefined;

    const targetGroupIdForReason =
      sr === "병동이동" ? activity.targetGroupId || null : null;

    if (!shouldInclude && existingAssignmentId != null) {
      payloads.push({
        operation: "cancel",
        assignment_id: existingAssignmentId,
      });
      continue;
    }

    if (!shouldInclude) {
      continue;
    }

    if (existingAssignmentId != null && existingAssignmentForReason) {
      const before = comparableInboundFields(
        existingAssignmentForReason,
        formatDateToString
      );
      const after = {
        start: activity.startDate,
        end: activity.endDate,
        target: activity.targetGroupId ?? "",
        memo: activity.memo ?? null,
        target_weekly_off_enabled: before.target_weekly_off_enabled,
      };
      const unchanged =
        before.start === after.start &&
        before.end === after.end &&
        before.target === after.target &&
        before.memo === after.memo &&
        before.target_weekly_off_enabled === after.target_weekly_off_enabled;
      if (unchanged) continue;

      const patch: AssignmentPatchPayload = {
        operation: "update",
        assignment_id: existingAssignmentId,
        reason: sr,
      };
      if (before.start !== after.start) patch.start_date = after.start;
      if (before.end !== after.end) patch.expected_end_date = after.end || null;
      if (sr === "병동이동" && before.target !== after.target) {
        patch.target_group_id = targetGroupIdForReason;
      }
      if (before.memo !== after.memo) patch.note = after.memo;
      payloads.push(patch);
      continue;
    }

    payloads.push({
      operation: "create",
      reason: sr,
      source_group_id: nurseDetailData.group_id,
      target_group_id: targetGroupIdForReason,
      office_id: nurseDetailData.office_id,
      start_date: activity.startDate,
      expected_end_date: activity.endDate || null,
    });
  }

  return payloads;
}

function buildPrecepteeSelfPeriodPayload(
  nurseDetailData: NurseDetailResponse,
  preceptorMode: "preceptor" | "preceptee" | null,
  preceptorId: string | null,
  precepteeAssignmentPeriod: PrecepteeAssignmentPeriod,
  formatDateToString: (d: Date | string | null | undefined) => string
): PrecepteeSelfPeriodPayload | null {
  // 진실 = nurse_preceptee_period(SSOT). 응답의 preceptee_period 에서 현재 관계 읽음.
  const existing = nurseDetailData.preceptee_period;
  const hasExisting = !!(existing && existing.preceptor_id);

  // 프리셉티 모드 아님/프리셉터 미선택 → 기존 관계 있으면 취소(=삭제).
  if (preceptorMode !== "preceptee" || !preceptorId) {
    return hasExisting ? { operation: "cancel" } : null;
  }

  if (!precepteeAssignmentPeriod.startDate) return null;

  const afterStart = precepteeAssignmentPeriod.startDate;
  const afterEnd = precepteeAssignmentPeriod.expectedEndDate || null;

  if (hasExisting) {
    const beforeStart = formatAssignmentDate(existing!.start_date, formatDateToString);
    const beforeEnd = existing!.expected_end_date
      ? formatAssignmentDate(existing!.expected_end_date, formatDateToString)
      : "";
    const samePreceptor = String(existing!.preceptor_id) === String(preceptorId);
    if (samePreceptor && beforeStart === afterStart && (beforeEnd || "") === (afterEnd || "")) {
      return null; // 변경 없음
    }
    return {
      operation: "update",
      preceptor_id: preceptorId,
      start_date: afterStart,
      expected_end_date: afterEnd,
    };
  }

  return {
    operation: "create",
    preceptor_id: preceptorId,
    start_date: afterStart,
    expected_end_date: afterEnd,
  };
}

function buildPreceptorPeerUpdatePayloads({
  initialByNurseId,
  selectedPrecepteeIds,
  draftByNurseId,
  defaultStartDate,
}: {
  initialByNurseId: Record<string, PreceptorPeerAssignmentDraft>;
  selectedPrecepteeIds: string[];
  draftByNurseId: Record<string, PreceptorPeerAssignmentDraft>;
  defaultStartDate: string;
}): PreceptorPeerUpdatePayload[] {
  const payloads: PreceptorPeerUpdatePayload[] = [];
  const selectedSet = new Set(selectedPrecepteeIds);

  // 기존 프리셉티였는데 선택 해제됨 → cancel(=삭제). period SSOT: target_nurse_id 로 식별.
  for (const targetNurseId of Object.keys(initialByNurseId)) {
    if (selectedSet.has(targetNurseId)) continue;
    payloads.push({ operation: "cancel", target_nurse_id: targetNurseId });
  }

  for (const targetNurseId of selectedPrecepteeIds) {
    const wasPreceptee = targetNurseId in initialByNurseId;
    const initial = initialByNurseId[targetNurseId];
    const draft = draftByNurseId[targetNurseId] ?? {
      startDate: defaultStartDate,
      expectedEndDate: "",
      note: null,
    };

    // create/update 모두 start/end 동봉(종료예정일 필수 — 백엔드가 검증).
    if (!wasPreceptee) {
      payloads.push({
        operation: "create",
        target_nurse_id: targetNurseId,
        start_date: draft.startDate || defaultStartDate,
        expected_end_date: draft.expectedEndDate || null,
      });
      continue;
    }

    // 변경 없으면 skip
    const startChanged = (initial?.startDate ?? "") !== (draft.startDate ?? "");
    const endChanged = (initial?.expectedEndDate ?? "") !== (draft.expectedEndDate ?? "");
    if (!startChanged && !endChanged) continue;

    payloads.push({
      operation: "update",
      target_nurse_id: targetNurseId,
      start_date: draft.startDate || defaultStartDate,
      expected_end_date: draft.expectedEndDate || null,
    });
  }

  return payloads;
}

function createEmptyReasonActivityMap(): Record<
  SupportedReason,
  ReasonActivity
> {
  return {
    휴직: { startDate: "", endDate: "", memo: null, targetGroupId: "" },
    퇴사: { startDate: "", endDate: "", memo: null, targetGroupId: "" },
    파견: { startDate: "", endDate: "", memo: null, targetGroupId: "" },
    병동이동: { startDate: "", endDate: "", memo: null, targetGroupId: "" },
  };
}

export default function NurseEditSideProfile({
  onlyView,
  isOpen,
  onClose,
  nurseId,
  selectedGroupId,
  isAdmin,
  allNurses,
  disableShiftAndWantedEdit = false,
  year,
  month,
  teamsSchema: preloadedTeamsSchema,
  gradeData: preloadedGradeData,
  shiftsData: preloadedShiftsData,
  rosterConfig: preloadedRosterConfig,
  monthMembers: preloadedMonthMembers,
}: NurseEditSideProfileProps) {
  // 사이드프로필이 열렸을 때 간호사 상세 정보 조회
  const { data: nurseDetailData, isPending: isNurseDetailLoading } =
    useNurseDetail(nurseId, { groupId: selectedGroupId, year, month });

  /** 파견 타겟 `target_*`를 루트 필드에 반영한 조회용 상세 (표시·시드 전용) */
  const effectiveNurseDetail = useMemo(() => {
    if (!nurseDetailData) return null;
    const row = resolveDispatchTargetRow(nurseDetailData, selectedGroupId);
    return applyInboundTargetForDisplay(nurseDetailData, row);
  }, [nurseDetailData, selectedGroupId]);

  const loginUser = useLoginUser();

  // teamsSchema는 커스텀 훅으로 직접 가져오기 (prop drilling 제거)
  const { data: fetchedTeamsSchema = [] } = useTeamsQuery(
    selectedGroupId,
    undefined,
    undefined,
    { enabled: !preloadedTeamsSchema }
  );
  const teamsSchema = preloadedTeamsSchema ?? fetchedTeamsSchema;

  // 로딩 중에도 이름을 표시하기 위해 allNurses에서 찾기
  const nurseNameFromList = useMemo(() => {
    return allNurses.find((n) => n.nurse_id === nurseId)?.name || "";
  }, [allNurses, nurseId]);

  const nurseNameById = useMemo(
    () => new Map(allNurses.map((nurse) => [nurse.nurse_id, nurse.name])),
    [allNurses]
  );

  const selectedPrecepteePeriod = nurseDetailData?.preceptee_period ?? null;
  const selectedPreceptorPeriodPeers = useMemo(() => {
    return (nurseDetailData?.preceptor_periods ?? [])
      .map((period) => ({
        nurse_id: period.nurse_id,
        name: nurseNameById.get(period.nurse_id) ?? period.nurse_id,
        start_date: period.start_date,
        expected_end_date: period.expected_end_date ?? null,
      }));
  }, [nurseDetailData?.preceptor_periods, nurseNameById]);

  const effectivePreceptorId = useMemo(() => {
    return selectedPrecepteePeriod?.preceptor_id ?? null;
  }, [selectedPrecepteePeriod?.preceptor_id]);

  // nurseDetailData를 NurseResponse 형태로 변환하는 헬퍼
  const nurseData = useMemo(() => {
    if (!nurseDetailData) return undefined;
    return {
      account_id: nurseDetailData.account_id,
      active: nurseDetailData.active,
      experience: nurseDetailData.experience,
      group_id: nurseDetailData.group_id,
      is_head_nurse: nurseDetailData.is_head_nurse,
      allowed_shifts: nurseDetailData.allowed_shifts as UnionShift[],
      joining_date: nurseDetailData.joining_date
        ? new Date(nurseDetailData.joining_date)
        : null,
      level_: nurseDetailData.level_ ?? "",
      name: nurseDetailData.name,
      nurse_id: nurseDetailData.nurse_id,
      office_id: nurseDetailData.office_id,
      personal_off_adjustment: nurseDetailData.personal_off_adjustment,
      preceptor_id: effectivePreceptorId,
      exclusion_partner_id: nurseDetailData.exclusion_partner_id ?? null,
      resignation_date: nurseDetailData.resignation_date
        ? new Date(nurseDetailData.resignation_date)
        : null,
      role: nurseDetailData.role,
      sequence: nurseDetailData.sequence,
      grade: effectiveNurseDetail?.grade ?? nurseDetailData.grade,
      weekly_off_weekday: nurseDetailData.weekly_off_weekday
        ? Number(nurseDetailData.weekly_off_weekday)
        : null,
      emp_num: nurseDetailData.emp_num,
      nurse_memo: nurseDetailData.nurse_memo,
      age: nurseDetailData.age,
      phone_number: nurseDetailData.phone_number,
      gender: nurseDetailData.gender,
      team_id: nurseDetailData.team_id ? Number(nurseDetailData.team_id) : null,
      is_weekend_off: nurseDetailData.is_weekend_off,
      fixed_shift: nurseDetailData.fixed_shift,
      enable_aide: nurseDetailData.enable_aide,
      enable_nurse_pair_preference:
        nurseDetailData.enable_nurse_pair_preference,
      wanted_max_requests: nurseDetailData.wanted_max_requests,
      birth_date: nurseDetailData.birth_date,
      resignation_reason: nurseDetailData.resignation_reason,
      resignation_reason_memo: nurseDetailData.resignation_reason_memo,
    } as NurseResponse;
  }, [nurseDetailData, effectiveNurseDetail, effectivePreceptorId]);

  const officeIdForByOffice = useMemo(() => {
    const fromDetail = nurseDetailData?.office_id?.trim() ?? "";
    const fromSession = loginUser?.office_id?.trim() ?? "";
    return fromDetail || fromSession;
  }, [nurseDetailData?.office_id, loginUser?.office_id]);

  /** 마스터/HN·수간호사 등에서 GET /groups/by-office 200 — 병동 선택·hn_id 매핑 */
  const fetchGroupsByOfficeEnabled = useMemo(() => {
    return (
      !!isAdmin ||
      loginUser?.is_master_admin ||
      loginUser?.hn_auth === "HN" ||
      loginUser?.is_head_nurse === true
    );
  }, [isAdmin, loginUser]);

  // groups 데이터는 그룹 관리자 권한 확인 및 관리 그룹 선택을 위해 필요
  // detail API에는 groups 정보가 포함되지 않으므로 별도 조회 필요
  // TODO: detail API에 managed_group_ids나 is_group_admin이 포함되면 이 쿼리 제거 가능
  const { data: groups } = useGroupsByOfficeQuery({
    enabled: fetchGroupsByOfficeEnabled,
    officeId: officeIdForByOffice,
  });

  // 모달이 열릴 때 main 컨테이너 스크롤 막기
  useEffect(() => {
    if (isOpen) {
      const mainContainer = document.querySelector(".c_main__container");
      if (mainContainer) {
        (mainContainer as HTMLElement).style.overflow = "hidden";
      }

      return () => {
        const mainContainer = document.querySelector(".c_main__container");
        if (mainContainer) {
          (mainContainer as HTMLElement).style.overflow = "";
        }
      };
    }
  }, [isOpen]);

  const throttle = useThrottle(1500);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isGroupAdminChangeModalOpen, setIsGroupAdminChangeModalOpen] =
    useState(false);
  const [isNightWeekendBlockedModalOpen, setIsNightWeekendBlockeModalOpen] =
    useState(false);
  const [reasonValidationErrors, setReasonValidationErrors] =
    useState<ReasonValidationErrors>({
      startDate: false,
      endDate: false,
      targetGroupId: false,
    });

  // 관리 그룹 드롭다운 상태 추가
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
  const groupDropdownRef = useRef<HTMLDivElement>(null);

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target as Node)
      ) {
        setIsGroupDropdownOpen(false);
      }
    };

    if (isGroupDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isGroupDropdownOpen]);

  // Date를 문자열로 변환하는 함수
  const formatDateToString = useCallback((
    date: Date | string | null | undefined
  ): string => {
    if (!date) return "";
    return DateUtils.dateFormatKR(date, "YYYY-MM-DD");
  }, []);

  const gradeConfigQuery = useGradeConfig({
    selectedGroupId: selectedGroupId!,
    enabled: !preloadedGradeData,
  });
  const data = preloadedGradeData ?? gradeConfigQuery.data;

  const rosterConfigQuery = useRosterConfig({
    enabled: !preloadedRosterConfig,
  });
  const serverConfig = preloadedRosterConfig ?? rosterConfigQuery.data;
  const isUseMid = serverConfig?.use_mid;

  // 선택 월 소속(시점 팀). year/month 둘 다 있을 때만 조회되며(useGroupMembers enabled),
  // 없으면 비활성 → 아래 currentTeam 이 캐시 멤버십 폴백으로 동작(기존 동작 보존).
  const monthMembersQuery = useGroupMembers(selectedGroupId, year, month, {
    enabled: !preloadedMonthMembers,
  });
  const monthMembers = preloadedMonthMembers ?? monthMembersQuery.data;

  const currentTeam = useMemo(() => {
    if (!nurseId) return "";
    const member = (monthMembers?.members ?? []).find(
      (m) => m.nurse_id === nurseId
    );
    if (member) {
      // 근무자관리·근무표생성과 동일 idiom: as_of_team(시점 팀), 없으면 캐시 team_id 폴백
      // → teamsSchema 로 team_name 해석. N전담도 직접 배정한 팀을 표시.
      const cacheTeamId =
        nurseDetailData?.team_id != null
          ? Number(nurseDetailData.team_id)
          : null;
      const teamId = member.as_of_team ?? cacheTeamId;
      if (teamId == null) return "";
      return teamsSchema.find((t) => t.team_id === teamId)?.team_name ?? "";
    }
    // 시점 정보 없음(월 미지정/명단 밖) → 기존 캐시 멤버십 기준 폴백.
    return findNurseTeamName(nurseId ?? undefined, teamsSchema);
  }, [nurseId, monthMembers, nurseDetailData?.team_id, teamsSchema]);

  const [formData, setFormData] = useState({
    role: "RN",
    level_: "",
    grade: 0,
    team: "",
    experience: "",
    allowed_shifts: [] as string[],
    joining_date: "",
    assignment_start_date: "",
    resignation_date: "",
    target_group_id: "",
    resignation_reason: null as string | null,
    resignation_reason_memo: null as string | null,
    nurse_memo: "",
    is_head_nurse: false,
    weekly_off_weekday: null as number | null,
    preceptor_id: null as string | null,
    exclusion_partner_id: null as string | null,
    is_weekend_off: false,
    fixed_shift: null as string | null,
    work_shifts: [] as string[],
    enable_aide: false,
    enable_nurse_pair_preference: false,
    wanted_max_requests: null as number | null,
    // TODO : 필드명 임시로 지어둠 / 백엔드 되면 수정필요
    is_group_admin: false, // 그룹 관리자 권한
    managed_group_ids: [] as string[], // 관리 그룹 ID 목록
    reason_activity: createEmptyReasonActivityMap() as Record<
      SupportedReason,
      ReasonActivity
    >,
    dispatch_activities: [] as DispatchActivity[],
  });

  /** `gradeSelectOptions`의 label 표시, 목록에 없는 값은 `grade_names`/키로 보정 */
  const gradeSelectItems = useMemo(() => {
    const opts =
      data?.gradeSelectOptions?.length && data.gradeSelectOptions.length > 0
        ? data.gradeSelectOptions
        : [];
    const base = opts.map((o) => ({
      key: `opt-${o.value}`,
      value: o.value,
      label: o.label,
    }));
    const valueSet = new Set(base.map((x) => x.value));
    if (formData.grade > 0 && !valueSet.has(String(formData.grade))) {
      base.push({
        key: `orphan-${formData.grade}`,
        value: String(formData.grade),
        label: resolveGradeLabel(formData.grade, opts, data?.grade_names),
      });
    }
    return base;
  }, [data?.gradeSelectOptions, data?.grade_names, formData.grade]);

  const teamIdSet = useMemo(
    () => new Set((teamsSchema ?? []).map((t) => String(t.team_id))),
    [teamsSchema]
  );

  /**
   * 빈 팀·스키마에 없는 team_id는 Radix Select가 매칭 실패로 **빈 칸**만 보임 → "none"(없음)으로 고정.
   * 팀 목록 로딩 전에는 기존 문자열 유지(로드 후 재판별).
   */
  const teamSelectValue = useMemo(() => {
    const raw = (formData.team ?? "").trim();
    if (!raw) return "none";
    if (teamIdSet.size === 0) return raw;
    return teamIdSet.has(raw) ? raw : "none";
  }, [formData.team, teamIdSet]);

  useEffect(() => {
    if (teamsSchema.length === 0) return;
    setFormData((prev) => {
      const raw = (prev.team ?? "").trim();
      if (!raw) return prev;
      const ids = new Set(teamsSchema.map((t) => String(t.team_id)));
      if (ids.has(raw)) return prev;
      return { ...prev, team: "" };
    });
  }, [teamsSchema]);

  useEffect(() => {
    if (!nurseId || teamsSchema.length === 0) return;

    const assignedTeam = teamsSchema.find((team) =>
      team.team_members.includes(nurseId)
    );
    const nextTeamId = assignedTeam ? String(assignedTeam.team_id) : "";

    setFormData((prev) =>
      prev.team === nextTeamId ? prev : { ...prev, team: nextTeamId }
    );
  }, [nurseId, teamsSchema]);

  const shiftsQuery = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? "") : "",
    { enabled: !preloadedShiftsData }
  );
  const shiftsRes = preloadedShiftsData ?? shiftsQuery.data;

  // Shift 인스턴스 생성
  const shiftMapper = useMemo(() => {
    if (!shiftsRes) return null;
    return new ShiftMapper(shiftsRes as unknown as MapperShift[]);
  }, [shiftsRes]);

  // 프리셉터 로직 훅 사용 (계산 로직)
  const {
    preceptorStatus,
    currentPreceptees,
    availablePreceptees,
    preceptorList,
  } = useNurseEditLogic({
    nurseData,
    allNurses,
  });

  const precepteeOptionsForSection = useMemo(() => {
    const options = new Map(availablePreceptees.map((item) => [item.value, item]));
    selectedPreceptorPeriodPeers.forEach((peer) => {
      if (!options.has(peer.nurse_id)) {
        options.set(peer.nurse_id, {
          label: peer.name,
          value: peer.nurse_id,
        });
      }
    });
    return Array.from(options.values());
  }, [
    availablePreceptees,
    selectedPreceptorPeriodPeers,
  ]);

  const precepteeAssignmentPeriod = useMemo(() => {
    if (selectedPrecepteePeriod) {
      return {
        startDate: formatAssignmentDate(
          selectedPrecepteePeriod.start_date,
          formatDateToString
        ),
        expectedEndDate: formatAssignmentDate(
          selectedPrecepteePeriod.expected_end_date,
          formatDateToString
        ),
      };
    }
    return null;
  }, [
    selectedPrecepteePeriod,
    formatDateToString,
  ]);

  const inboundReasonTabKeys = useMemo(() => {
    if (!nurseDetailData) return new Set<SupportedReason>();
    const list = getActiveAssignmentsFromDetail(nurseDetailData);
    const s = new Set<SupportedReason>();
    for (const a of list) {
      const r = normalizeAssignmentReason(a.reason);
      if (MANAGED_REASONS_SET.has(r)) {
        s.add(r as SupportedReason);
      }
    }
    return s;
  }, [nurseDetailData]);

  const hasLocalReasonData = useCallback(
    (reason: SupportedReason) => {
      if (reason === "파견") {
        return formData.dispatch_activities.some((row) =>
          Boolean(
            row.startDate ||
            row.endDate ||
            row.targetGroupId ||
            (row.memo ?? "").trim()
          )
        );
      }
      const activity = formData.reason_activity[reason];
      return Boolean(
        activity.startDate ||
        activity.endDate ||
        activity.targetGroupId ||
        (activity.memo ?? "").trim()
      );
    },
    [formData.dispatch_activities, formData.reason_activity]
  );

  const reasonHasInboundData = useCallback(
    (reason: SupportedReason) => {
      if (hasLocalReasonData(reason)) return true;
      return inboundReasonTabKeys.has(reason);
    },
    [hasLocalReasonData, inboundReasonTabKeys]
  );

  // 프리셉터 관련 상태
  const [preceptorMode, setPreceptorMode] = useState<
    "preceptor" | "preceptee" | null
  >(null);
  const [selectedPreceptees, setSelectedPreceptees] = useState<string[]>([]);
  const [preceptorAssignmentDraftByNurseId, setPreceptorAssignmentDraftByNurseId] =
    useState<Record<string, PreceptorPeerAssignmentDraft>>({});
  const [precepteeAssignmentPeriodDraft, setPrecepteeAssignmentPeriodDraft] =
    useState({
      startDate: "",
      expectedEndDate: "",
    });
  const [isPrecepteeStartDateError, setIsPrecepteeStartDateError] =
    useState(false);
  const defaultPreceptorAssignmentStartDate = useMemo(
    () => DateUtils.dateFormatKR(new Date(), "YYYY-MM-DD"),
    []
  );

  const exclusionBlockedNurseIds = useMemo(() => {
    const blocked = new Set<string>();
    if (selectedPrecepteePeriod?.preceptor_id) {
      blocked.add(selectedPrecepteePeriod.preceptor_id);
    }
    selectedPreceptorPeriodPeers.forEach((peer) => blocked.add(peer.nurse_id));
    if (preceptorMode === "preceptee" && formData.preceptor_id) {
      blocked.add(formData.preceptor_id);
    }
    if (preceptorMode === "preceptor") {
      selectedPreceptees.forEach((precepteeId) => blocked.add(precepteeId));
    }
    return blocked;
  }, [
    formData.preceptor_id,
    preceptorMode,
    selectedPrecepteePeriod?.preceptor_id,
    selectedPreceptorPeriodPeers,
    selectedPreceptees,
  ]);

  const effectiveExclusionPartnerId = useMemo(() => {
    const selected = formData.exclusion_partner_id;
    if (!selected) return null;
    return exclusionBlockedNurseIds.has(selected) ? null : selected;
  }, [exclusionBlockedNurseIds, formData.exclusion_partner_id]);

  const exclusionPartnerOptions = useMemo(() => {
    return allNurses
      .filter((nurse) => nurse.nurse_id !== nurseId)
      .filter((nurse) => nurse.active === 1)
      .filter((nurse) => !exclusionBlockedNurseIds.has(nurse.nurse_id))
      .map((nurse) => ({
        value: nurse.nurse_id,
        label: nurse.name || nurse.nurse_id,
      }));
  }, [allNurses, exclusionBlockedNurseIds, nurseId]);

  const isExclusionPartnerBlocked = Boolean(
    formData.exclusion_partner_id &&
      exclusionBlockedNurseIds.has(formData.exclusion_partner_id)
  );

  useEffect(() => {
    setPrecepteeAssignmentPeriodDraft((prev) => {
      const next = {
        startDate: precepteeAssignmentPeriod?.startDate ?? "",
        expectedEndDate: precepteeAssignmentPeriod?.expectedEndDate ?? "",
      };
      return prev.startDate === next.startDate &&
        prev.expectedEndDate === next.expectedEndDate
        ? prev
        : next;
    });
    setIsPrecepteeStartDateError(false);
  }, [precepteeAssignmentPeriod]);

  useEffect(() => {
    if (preceptorMode !== "preceptee" || !formData.preceptor_id) return;

    setPrecepteeAssignmentPeriodDraft((prev) => {
      if (prev.startDate) return prev;
      return {
        ...prev,
        startDate: DateUtils.dateFormatKR(new Date(), "YYYY-MM-DD"),
      };
    });
  }, [formData.preceptor_id, preceptorMode]);


  useEffect(() => {
    if (preceptorMode !== "preceptor" || selectedPreceptees.length === 0)
      return;

    setPrecepteeAssignmentPeriodDraft((prev) => {
      const next = {
        startDate: DateUtils.dateFormatKR(new Date(), "YYYY-MM-DD"),
        expectedEndDate: "",
      };
      if (
        prev.startDate === next.startDate &&
        prev.expectedEndDate === next.expectedEndDate
      ) {
        return prev;
      }
      return {
        ...next,
      };
    });
  }, [preceptorMode, selectedPreceptees.length]);

  const handlePrecepteesChange = useCallback(
    (values: string[]) => {
      setSelectedPreceptees(values);
      setPreceptorAssignmentDraftByNurseId((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const targetNurseId of values) {
          if (next[targetNurseId]) continue;
          next[targetNurseId] = {
            startDate: defaultPreceptorAssignmentStartDate,
            expectedEndDate: "",
            note: null,
          };
          changed = true;
        }
        return changed ? next : prev;
      });
    },
    [defaultPreceptorAssignmentStartDate]
  );

  const handlePreceptorAssignmentPeriodChange = useCallback(
    (targetNurseId: string, period: PrecepteeAssignmentPeriod) => {
      setPreceptorAssignmentDraftByNurseId((prev) => ({
        ...prev,
        [targetNurseId]: {
          ...prev[targetNurseId],
          startDate: period.startDate || defaultPreceptorAssignmentStartDate,
          expectedEndDate: period.expectedEndDate,
        },
      }));
    },
    [defaultPreceptorAssignmentStartDate]
  );
  // 초기 프리셉터 모드 및 선택된 프리셉티 설정
  useEffect(() => {
    if (nurseDetailData) {
      const detailPrecepteeIds = selectedPreceptorPeriodPeers.map(
        (peer) => peer.nurse_id
      );
      const draftByNurseId = Object.fromEntries(
        selectedPreceptorPeriodPeers.map((peer) => [
          peer.nurse_id,
          {
            // period SSOT: assignment_id 폐기, target_nurse_id(peer.nurse_id) 로 식별.
            assignmentId: undefined,
            startDate: peer.start_date ?? defaultPreceptorAssignmentStartDate,
            expectedEndDate: peer.expected_end_date ?? "",
            note: null,
          },
        ])
      ) as Record<string, PreceptorPeerAssignmentDraft>;

      initialPreceptorAssignmentByNurseIdRef.current = draftByNurseId;
      setPreceptorAssignmentDraftByNurseId(draftByNurseId);

      if (detailPrecepteeIds.length > 0 || preceptorStatus.isPreceptor) {
        setPreceptorMode("preceptor");
        setSelectedPreceptees(
          detailPrecepteeIds.length > 0 ? detailPrecepteeIds : currentPreceptees
        );
      } else if (preceptorStatus.isPreceptee) {
        setPreceptorMode("preceptee");
        setSelectedPreceptees([]);
      } else {
        setPreceptorMode(null);
        setSelectedPreceptees([]);
      }
    }
  }, [
    currentPreceptees,
    defaultPreceptorAssignmentStartDate,
    nurseDetailData,
    preceptorStatus,
    selectedPreceptorPeriodPeers,
  ]);

  // 프리셉터/프리셉티 버튼 클릭 핸들러
  const handlePreceptorModeChange = useCallback(
    (mode: "preceptor" | "preceptee") => {
      setIsPrecepteeStartDateError(false);
      if (preceptorMode === mode) {
        setPreceptorMode(null);
        setSelectedPreceptees([]);
        setFormData((prev) => ({ ...prev, preceptor_id: null }));
      } else {
        setPreceptorMode(mode);
        if (mode === "preceptor") {
          const seededPreceptees = Object.keys(
            initialPreceptorAssignmentByNurseIdRef.current
          );
          handlePrecepteesChange(
            seededPreceptees.length > 0 ? seededPreceptees : currentPreceptees
          );
          setFormData((prev) => ({ ...prev, preceptor_id: null }));
        } else {
          setSelectedPreceptees([]);
        }
      }
    },
    [currentPreceptees, handlePrecepteesChange, preceptorMode]
  );

  // 전담여부 토글 함수 (D: 데이, E: 이브닝, N: 나이트)
  // 빈 배열 = 모든 근무형태 활성화, 배열에 값 = 포함 목록
  const toggleNightNurse = useCallback(
    (type: string) => {
      setFormData((prev) => {
        const currentShifts = prev.allowed_shifts as string[];
        // 빈 배열 = 모든 것이 활성화된 상태
        // 배열에 값이 있으면 = 해당 값들만 포함된 상태
        const isCurrentlyActive =
          currentShifts.length === 0 || currentShifts.includes(type);

        if (isCurrentlyActive) {
          // 활성화된 상태에서 토글 = 포함 목록에서 제거
          // 빈 배열이면 모든 옵션을 포함 목록으로 변환 후 해당 타입 제거
          if (currentShifts.length === 0) {
            // 모든 옵션에서 현재 타입만 제외
            const allTypes = ["D", "E", "N"];
            if (isUseMid !== false) {
              allTypes.push("M");
            }
            const newShifts = allTypes.filter((s) => s !== type);
            const sortedShifts = sortShiftArray(newShifts);
            return { ...prev, allowed_shifts: sortedShifts };
          } else {
            // 포함 목록에서 제거
            const newShifts = currentShifts.filter((s) => s !== type);
            return { ...prev, allowed_shifts: newShifts };
          }
        } else {
          // 비활성화된 상태에서 토글 = 포함 목록에 추가
          const newShifts = [...currentShifts, type];
          const sortedShifts = sortShiftArray(newShifts);
          return { ...prev, allowed_shifts: sortedShifts };
        }
      });
    },
    [isUseMid]
  );

  const handleWeekendOffChange = useCallback((value: boolean) => {
    setFormData((prev) => ({ ...prev, is_weekend_off: value }));
  }, []);

  const handleFixedShiftChange = useCallback((value: string | null) => {
    setFormData((prev) => ({ ...prev, fixed_shift: value }));
  }, []);

  // 내 주휴 보기 호출
  const weeklyOffCurrentDate = useMemo(() => new Date(), []);
  const weeklyOffYear = year ?? weeklyOffCurrentDate.getFullYear();
  const weeklyOffMonth = month ?? weeklyOffCurrentDate.getMonth() + 1;

  const { data: weeklyOffData } = useQuery<{
    my_weekly_off_weekday_base?: number | null;
  } | null>({
    queryKey: QUERY_KEYS.weeklyOff.my(
      weeklyOffYear,
      weeklyOffMonth,
      nurseId ?? undefined
    ),
    queryFn: async () => {
      if (!nurseId) return null;
      return httpClient.get<{ my_weekly_off_weekday_base?: number | null }>(
        "/weekly-off/my",
        {
        params: {
          year: weeklyOffYear,
          month: weeklyOffMonth,
          nurse_id: nurseId,
        },
        }
      );
    },
    enabled: !!nurseId,
    staleTime: 5 * 60 * 1000,
  });

  // 주휴 설정 조회 (순환주기 시작일 등)
  const { data: weeklyOffSettingsData } = useWeeklyOffSettingsQuery({
    isAdmin: isAdmin,
    groupId: selectedGroupId ?? undefined,
  });

  useEffect(() => {
    const targetRow = nurseDetailData
      ? resolveDispatchTargetRow(nurseDetailData, selectedGroupId)
      : null;

    // 파견 inbound의 target 주휴요일이 있으면 내 주휴 API보다 우선 (항상 반영)
    if (targetRow?.target_weekly_off_weekday != null) {
      const w = targetRow.target_weekly_off_weekday;
      setFormData((prev) => ({ ...prev, weekly_off_weekday: w }));
      initialNurseProfilePatchRef.current = {
        ...initialNurseProfilePatchRef.current,
        weekly_off_weekday: w,
      };
      return;
    }

    if (weeklyOffData?.my_weekly_off_weekday_base === undefined) return;
    const v = weeklyOffData.my_weekly_off_weekday_base;
    setFormData((prev) => ({
      ...prev,
      weekly_off_weekday: v,
    }));
    initialNurseProfilePatchRef.current = {
      ...initialNurseProfilePatchRef.current,
      weekly_off_weekday: v,
    };
  }, [weeklyOffData, nurseDetailData, selectedGroupId]);

  // nurseId가 변경될 때만 formData 초기화 (무한 루프 방지)
  const prevNurseIdRef = useRef<string | null>(null);
  /** 진입 시점 활성 배정 스냅샷 — 저장 시 배정 diff·cancel 판별 */
  const initialActiveAssignmentsRef = useRef<NurseInboundListItem[]>([]);
  /** 진입 시점 프로필 PATCH 스냅샷 — 부분 갱신 diff */
  const initialNurseProfilePatchRef = useRef<Record<string, unknown>>({});
  const initialPreceptorAssignmentByNurseIdRef = useRef<
    Record<string, PreceptorPeerAssignmentDraft>
  >({});
  // groups 비동기 도착 시 그룹 관리자 상태를 간호사별 1회 동기화
  const groupAdminSyncedNurseIdRef = useRef<string | null>(null);

  useEffect(() => {
    // nurseId가 변경되었을 때만 실행
    if (!nurseDetailData) return;

    if (nurseDetailData.nurse_id !== prevNurseIdRef.current) {
      prevNurseIdRef.current = nurseDetailData.nurse_id;
      groupAdminSyncedNurseIdRef.current = null;

      const initTeam = teamsSchema?.find((t) => t.team_name === currentTeam);

      // groups의 hn_id 배열에서 현재 간호사가 포함된 그룹 ID 찾기
      const initialManagedGroupIds = groups
        ? groups
            .filter((group) => group.hn_id?.includes(nurseDetailData.nurse_id))
            .map((group) => group.group_id)
        : [];

      // hn_id에 포함되어 있으면 자동으로 그룹 관리자 권한 true
      const isGroupAdminFromGroups = groups
        ? groups.some((group) =>
            group.hn_id?.includes(nurseDetailData.nurse_id)
          )
        : false;
      const activeAssignments = getActiveAssignmentsFromDetail(nurseDetailData);
      initialActiveAssignmentsRef.current = activeAssignments.slice();

      const inboundReason =
        activeAssignments.find((a) =>
          ["휴직", "퇴사", "파견", "병동이동", "부서이동"].includes(a.reason)
        )?.reason ?? "";
      const inferredReason = ((): SupportedReason | null => {
        if (
          nurseDetailData.resignation_reason &&
          ["휴직", "퇴사", "파견", "병동이동", "부서이동"].includes(
            nurseDetailData.resignation_reason
          )
        ) {
          return (
            nurseDetailData.resignation_reason === "부서이동"
              ? "병동이동"
              : nurseDetailData.resignation_reason
          ) as SupportedReason;
        }
        if (
          ["휴직", "퇴사", "파견", "병동이동", "부서이동"].includes(
            inboundReason
          )
        ) {
          return (
            inboundReason === "부서이동" ? "병동이동" : inboundReason
          ) as SupportedReason;
        }
        return null;
      })();

      const initialReasonActivity = createEmptyReasonActivityMap();
      const initialDispatchActivities: DispatchActivity[] = [];

      const seedForReason = (
        reason: SupportedReason
      ): NurseInboundListItem | undefined =>
        activeAssignments.find(
          (a) => normalizeAssignmentReason(a.reason) === reason
        );

      /** 파견·단건 사유 모두 활성 배정(inbound) 기준으로 채움 — 추론 탭과 무관 */
      const 파견Rows = activeAssignments.filter(
        (a) => normalizeAssignmentReason(a.reason) === "파견"
      );
      for (const row of 파견Rows) {
        initialDispatchActivities.push({
          assignmentId: typeof row.id === "number" ? row.id : undefined,
          startDate: row.startDate
            ? formatDateToString(new Date(row.startDate))
            : "",
          endDate: row.endDate ? formatDateToString(new Date(row.endDate)) : "",
          memo: row.note ?? null,
          targetGroupId: row.target_group_id ?? row.targetGroupId ?? "",
        });
      }
      if (initialDispatchActivities.length > 0) {
        const first = initialDispatchActivities[0];
        initialReasonActivity["파견"] = {
          startDate: first.startDate,
          endDate: first.endDate,
          memo: first.memo,
          targetGroupId: first.targetGroupId,
        };
      }

      for (const sr of SINGLE_MANAGED_REASONS) {
        const row = seedForReason(sr);
        if (!row) continue;
        initialReasonActivity[sr] = reasonActivityFromInboundRow(
          row,
          formatDateToString
        );
      }

      if (inferredReason === "퇴사") {
        initialReasonActivity["퇴사"] = {
          ...initialReasonActivity["퇴사"],
          endDate: nurseDetailData.resignation_date
            ? formatDateToString(new Date(nurseDetailData.resignation_date))
            : initialReasonActivity["퇴사"].endDate,
          memo:
            nurseDetailData.resignation_reason_memo ??
            initialReasonActivity["퇴사"].memo,
        };
      }

      const teamFromEffective =
        effectiveNurseDetail!.team_id != null &&
        effectiveNurseDetail!.team_id !== ""
          ? String(effectiveNurseDetail!.team_id)
          : "";
      const teamForm =
        teamFromEffective !== ""
          ? teamFromEffective
          : initTeam
            ? String(initTeam.team_id)
            : "";

      const weeklySeed =
        effectiveNurseDetail!.weekly_off_weekday ??
        weeklyOffData?.my_weekly_off_weekday_base ??
        null;

      const nextForm = {
        role: nurseDetailData.role || "RN",
        level_: nurseDetailData.level_ ?? "",
        grade: effectiveNurseDetail!.grade ?? 0,
        team: teamForm,
        experience:
          nurseDetailData.experience !== undefined
            ? String(nurseDetailData.experience)
            : "",
        allowed_shifts: Array.isArray(effectiveNurseDetail!.allowed_shifts)
          ? [...effectiveNurseDetail!.allowed_shifts]
          : [],
        joining_date: nurseDetailData.joining_date
          ? formatDateToString(new Date(nurseDetailData.joining_date))
          : "",
        assignment_start_date:
          inferredReason != null
            ? initialReasonActivity[inferredReason].startDate
            : "",
        target_group_id:
          inferredReason != null
            ? initialReasonActivity[inferredReason].targetGroupId
            : "",
        resignation_date: nurseDetailData.resignation_date
          ? formatDateToString(new Date(nurseDetailData.resignation_date))
          : inferredReason != null
            ? initialReasonActivity[inferredReason].endDate
            : "",
        resignation_reason: inferredReason,
        resignation_reason_memo:
          nurseDetailData.resignation_reason_memo ?? null,
        nurse_memo: nurseDetailData.nurse_memo || "",
        is_head_nurse: nurseDetailData.is_head_nurse || false,
        weekly_off_weekday: weeklySeed,
        preceptor_id: effectivePreceptorId,
        exclusion_partner_id: nurseDetailData.exclusion_partner_id ?? null,
        is_weekend_off: effectiveNurseDetail!.is_weekend_off,
        fixed_shift: effectiveNurseDetail!.fixed_shift ?? null,
        work_shifts: nurseDetailData.work_shifts || [],
        enable_aide: nurseDetailData.enable_aide ?? false,
        enable_nurse_pair_preference:
          nurseDetailData.enable_nurse_pair_preference ?? false,
        wanted_max_requests: effectiveNurseDetail!.wanted_max_requests ?? null,
        is_group_admin: isGroupAdminFromGroups,
        managed_group_ids: initialManagedGroupIds,
        reason_activity: initialReasonActivity,
        dispatch_activities: initialDispatchActivities,
      };

      const selReason = nextForm.resignation_reason as SupportedReason | null;
      const selActivity =
        selReason === "파견"
          ? (nextForm.dispatch_activities[0] ?? null)
          : selReason != null
            ? nextForm.reason_activity[selReason]
            : null;
      const initPayloadResignationDate = selActivity?.endDate ?? "";
      const initPayloadResignationMemo = selActivity?.memo ?? null;

      const seedPreceptorMode =
        selectedPreceptorPeriodPeers.length > 0 ||
        preceptorStatus.isPreceptor
        ? ("preceptor" as const)
        : preceptorStatus.isPreceptee
          ? ("preceptee" as const)
          : null;

      initialNurseProfilePatchRef.current = buildFullNurseProfilePatch({
        formData: nextForm,
        teamsSchema: teamsSchema ?? [],
        preceptorMode: seedPreceptorMode,
        payloadResignationDate: initPayloadResignationDate,
        payloadResignationMemo: initPayloadResignationMemo,
        omitIsWeekendOff:
          resolveDispatchTargetRow(nurseDetailData, selectedGroupId) != null,
      });

      setFormData(nextForm);
    }
    // nurse_id 변경 시에만 위 블록 실행 — preceptorStatus는 동일 렌더에서 간호사별로 최신
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 초기 스냅샷은 nurse 단위로만 재생성
  }, [
    nurseDetailData,
    effectiveNurseDetail,
    currentTeam,
    teamsSchema,
    groups,
    selectedPreceptorPeriodPeers,
    weeklyOffData?.my_weekly_off_weekday_base,
    selectedGroupId,
  ]);

  // nurse detail이 먼저 오고 groups가 늦게 와도 그룹 관리자 상태를 정확히 반영
  useEffect(() => {
    if (!nurseDetailData || !groups) return;

    const currentNurseId = nurseDetailData.nurse_id;
    if (groupAdminSyncedNurseIdRef.current === currentNurseId) return;

    const initialManagedGroupIds = groups
      .filter((group) => group.hn_id?.includes(currentNurseId))
      .map((group) => group.group_id);
    const isGroupAdminFromGroups = initialManagedGroupIds.length > 0;

    setFormData((prev) => ({
      ...prev,
      is_group_admin: isGroupAdminFromGroups,
      managed_group_ids: initialManagedGroupIds,
    }));

    groupAdminSyncedNurseIdRef.current = currentNurseId;
  }, [nurseDetailData, groups]);

  const handleSaveClick = () => {
    if (!nurseDetailData) return;

    // 근무형태가 'N' 이고 주말 휴무가 '적용'인 경우
    if (
      formData.allowed_shifts.length === 1 &&
      formData.allowed_shifts.includes("N") &&
      formData.is_weekend_off
    ) {
      setIsNightWeekendBlockeModalOpen(true);
      return;
    }

    // 프리셉터 모드일 때 프리셉티를 선택하지 않으면 저장 불가
    if (
      preceptorMode === "preceptor" &&
      selectedPreceptees.length === 0 &&
      Object.keys(initialPreceptorAssignmentByNurseIdRef.current).length === 0
    ) {
      alert("프리셉티를 선택해주세요.");
      return;
    }


    if (
      preceptorMode === "preceptee" &&
      formData.preceptor_id &&
      !precepteeAssignmentPeriodDraft.startDate
    ) {
      setIsPrecepteeStartDateError(true);
      alert("프리셉티 시작일을 선택해주세요.");
      return;
    }
    // 종료예정일 필수(무기한 프리셉티 폐지).
    if (
      preceptorMode === "preceptee" &&
      formData.preceptor_id &&
      !precepteeAssignmentPeriodDraft.expectedEndDate
    ) {
      alert("프리셉티 종료예정일을 선택해주세요.");
      return;
    }
    // 프리셉터 모드: 선택된 각 프리셉티의 종료예정일 필수.
    if (preceptorMode === "preceptor") {
      for (const pid of selectedPreceptees) {
        const d = preceptorAssignmentDraftByNurseId[pid];
        if (!d?.expectedEndDate) {
          alert("각 프리셉티의 종료예정일을 선택해주세요.");
          return;
        }
      }
    }

    // 프리셉티가 있는 프리셉터의 경우에 프리셉티로 모드변경하고 프리셉터를 셀렉트박스에서 선택한뒤(없음 포함) 저장을 누르면 변경할거냐는 alert
    if (preceptorStatus.isPreceptor && preceptorMode === "preceptee") {
      if (
        !confirm("현재 담당하고 있는 프리셉티가 있습니다.\n변경하시겠습니까?")
      ) {
        return;
      }
    }

    const normalizedReasonValue =
      formData.resignation_reason === "부서이동"
        ? "병동이동"
        : formData.resignation_reason;
    const selectedReason = (
      normalizedReasonValue &&
      ["휴직", "퇴사", "파견", "병동이동"].includes(normalizedReasonValue)
        ? normalizedReasonValue
        : null
    ) as SupportedReason | null;

    if (selectedReason != null) {
      const nextErrors: ReasonValidationErrors = {
        startDate: false,
        endDate: false,
        targetGroupId: false,
      };

      if (selectedReason === "파견") {
        const rows = formData.dispatch_activities;
        if (rows.length === 0) {
          nextErrors.startDate = true;
          nextErrors.endDate = true;
          nextErrors.targetGroupId = true;
        }
        for (const row of rows) {
          if (!row.startDate) nextErrors.startDate = true;
          if (!row.endDate) nextErrors.endDate = true;
          if (!row.targetGroupId) nextErrors.targetGroupId = true;
        }
      } else {
        const selectedReasonActivity = formData.reason_activity[selectedReason];
        if (
          (selectedReason === "휴직" || selectedReason === "병동이동") &&
          !selectedReasonActivity.startDate
        ) {
          nextErrors.startDate = true;
        }
        if (
          (selectedReason === "휴직" || selectedReason === "퇴사") &&
          !selectedReasonActivity.endDate
        ) {
          nextErrors.endDate = true;
        }
        if (
          selectedReason === "병동이동" &&
          !selectedReasonActivity.targetGroupId
        ) {
          nextErrors.targetGroupId = true;
        }
      }

      const hasError =
        nextErrors.startDate || nextErrors.endDate || nextErrors.targetGroupId;
      setReasonValidationErrors(nextErrors);
      if (hasError) return;
    } else {
      setReasonValidationErrors({
        startDate: false,
        endDate: false,
        targetGroupId: false,
      });
    }

    setIsSaveModalOpen(true);
  };
  const queryClient = useQueryClient();

  const handleGroupAdminUpdate = async () => {
    if (!isAdmin || !nurseDetailData) return;

    let originalIsGroupAdmin = false;
    let originalManagedGroupIds: string[] = [];

    if (groups) {
      const managedGroups = groups.filter((group) =>
        group.hn_id?.includes(nurseDetailData.nurse_id)
      );
      const isGroupAdminFromGroups = managedGroups.length > 0;

      originalIsGroupAdmin = isGroupAdminFromGroups;
      originalManagedGroupIds = managedGroups.map((g) => g.group_id);
    }

    const isGroupAdminChanged =
      originalIsGroupAdmin !== formData.is_group_admin;
    const managedGroupsChanged =
      JSON.stringify([...originalManagedGroupIds].sort()) !==
      JSON.stringify([...formData.managed_group_ids].sort());

    if (isGroupAdminChanged || managedGroupsChanged) {
      await httpClient.put("/groups/hn-admin", {
        nurse_id: nurseDetailData.nurse_id,
        group_ids: formData.is_group_admin ? formData.managed_group_ids : [],
      });
      // 그룹 목록 및 그룹 관리자 관련 쿼리 무효화
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
      await queryClient.invalidateQueries({ queryKey: ["auth/switch-group"] });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.groups.byOfficeScope(),
      });
      const groupIdForQuery = isAdmin ? (selectedGroupId ?? "") : "";
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.listScope(groupIdForQuery),
      });
    }
  };

  const saveNurseMutation = useMutation({
    mutationFn: async (params: {
      nursePatchBody: Record<string, unknown>;
      nurseId: string;
      groupIdForQuery: string;
    }) => {
      const { nursePatchBody, nurseId, groupIdForQuery } = params;

      // 월 셀렉터 선택월 → period valid_from(그 달부터 발효). 미설정 시 백엔드 today 폴백.
      const { year, month } = useNurseAsOfMonthStore.getState();
      const queryParams = new URLSearchParams();
      if (groupIdForQuery) queryParams.set("group_id", groupIdForQuery);
      if (year != null && month != null) {
        queryParams.set("year", String(year));
        queryParams.set("month", String(month));
      }
      const queryString = queryParams.toString();
      await httpClient.patch(
        `/nurses/${nurseId}${queryString ? `?${queryString}` : ""}`,
        nursePatchBody
      );

      // 프리셉터 관계 해제
      await handleGroupAdminUpdate();
      return { groupIdForQuery };
    },
    onSuccess: async ({ groupIdForQuery }) => {
      await Promise.all([
        // 간호사 목록/상세
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.listScope(groupIdForQuery),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.membersScope(
            groupIdForQuery || selectedGroupId
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.detailScope(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.precepteePeriodsScope(),
        }),
        // 팀/그룹
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.TEAMS, selectedGroupId ?? null],
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.groups.byOfficeScope(),
        }),
        // 근무표 관련
        queryClient.invalidateQueries({
          queryKey: ["ROSTER_ISSUED_ROSTER"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["ROSTER_ISSUED_ROSTER_ME"],
        }),
      ]);

      toast.success(
        `${nurseDetailData?.name || nurseNameFromList || "간호사"}님의 정보가 변경되었습니다.`
      );
      setIsSaveModalOpen(false);
      onClose();
    },
  });

  // 휴지통(삭제) 즉시 취소: 저장하기 없이 해당 배정만 cancel(프로필 유지, onClose/프리셉터해제 없음)
  const immediateCancelMutation = useMutation({
    mutationFn: async (assignmentId: number) => {
      if (!nurseDetailData) return null;
      await httpClient.patch(`/nurses/${nurseDetailData.nurse_id}`, {
        assignment: { operation: "cancel", assignment_id: assignmentId },
      });
      return {
        groupIdForQuery: isAdmin ? (selectedGroupId ?? "") : "",
      };
    },
    onSuccess: async (res) => {
      if (!res) return;
      const { groupIdForQuery } = res;
      // 같은 nurse_id면 폼 초기화 useEffect가 가드(prevNurseIdRef)에 막혀 재시드 안 됨
      // → null로 리셋해 detail refetch 후 폼(파견/병동이동 행)을 cancelled 반영본으로 재시드
      prevNurseIdRef.current = null;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.listScope(groupIdForQuery),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.membersScope(
            groupIdForQuery || selectedGroupId
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.detailScope(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.TEAMS, selectedGroupId ?? null],
        }),
        queryClient.invalidateQueries({ queryKey: ["ROSTER_ISSUED_ROSTER"] }),
        queryClient.invalidateQueries({ queryKey: ["ROSTER_ISSUED_ROSTER_ME"] }),
      ]);
      toast.success("배정이 취소되었습니다.");
    },
  });

  const handleImmediateCancelAssignment = (assignmentId: number) => {
    if (immediateCancelMutation.isPending) return;
    immediateCancelMutation.mutate(assignmentId);
  };

  const immediateDispatchMutation = useMutation({
    mutationFn: async (activity: DispatchActivity) => {
      if (!nurseDetailData) return null;

      const assignmentPayload: AssignmentPatchPayload =
        activity.assignmentId != null
          ? omitUndefinedValues({
              operation: "update",
              assignment_id: activity.assignmentId,
              reason: "파견",
              start_date: activity.startDate,
              expected_end_date: activity.endDate || null,
              target_group_id: activity.targetGroupId,
              note: activity.memo ?? null,
            })
          : omitUndefinedValues({
              operation: "create",
              reason: "파견",
              source_group_id: nurseDetailData.group_id,
              target_group_id: activity.targetGroupId,
              office_id: nurseDetailData.office_id,
              start_date: activity.startDate,
              expected_end_date: activity.endDate || null,
              note: activity.memo ?? null,
            });

      await httpClient.patch(`/nurses/${nurseDetailData.nurse_id}`, {
        assignment: assignmentPayload,
      });

      return {
        groupIdForQuery: isAdmin ? (selectedGroupId ?? "") : "",
      };
    },
    onSuccess: async (res) => {
      if (!res) return;
      const { groupIdForQuery } = res;
      prevNurseIdRef.current = null;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.listScope(groupIdForQuery),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.membersScope(
            groupIdForQuery || selectedGroupId
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.detailScope(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.TEAMS, selectedGroupId ?? null],
        }),
        queryClient.invalidateQueries({ queryKey: ["ROSTER_ISSUED_ROSTER"] }),
        queryClient.invalidateQueries({ queryKey: ["ROSTER_ISSUED_ROSTER_ME"] }),
      ]);
      toast.success("파견 기록이 저장되었습니다.");
    },
  });

  const handleImmediateDispatchSave = async (activity: DispatchActivity) => {
    if (immediateDispatchMutation.isPending) return;
    await immediateDispatchMutation.mutateAsync(activity);
  };

  const executeSave = async () => {
    if (
      !nurseDetailData ||
      saveNurseMutation.isPending ||
      immediateDispatchMutation.isPending
    )
      return;

    const targetTeam = teamsSchema?.find(
      (t) => String(t.team_id) === formData.team
    );
    const targetTeamId = targetTeam ? targetTeam.team_id : null;
    const selectedReason = (formData.resignation_reason ??
      null) as SupportedReason | null;
    const selectedReasonActivity =
      selectedReason === "파견"
        ? (formData.dispatch_activities[0] ?? null)
        : selectedReason != null
          ? formData.reason_activity[selectedReason]
          : null;
    const payloadResignationDate = selectedReasonActivity?.endDate ?? "";
    const payloadResignationMemo = selectedReasonActivity?.memo ?? null;

    const assignmentPatchAllowed = canIncludeAssignmentInNursePatch(
      onlyView ?? false,
      disableShiftAndWantedEdit,
      nurseDetailData,
      selectedGroupId
    );
    // target(전입 받은 병동) 뷰: 생성/수정은 source 전용으로 막되, 취소(cancel)만 허용한다.
    // 잘못 들어온 전입자 파견을 target 그룹에서 직접 취소→재등록 가능하게(백엔드도 cancel만 target 허용).
    const allAssignmentPayloads = buildAssignmentPayloads(
      nurseDetailData,
      selectedGroupId,
      {
        allowed_shifts: formData.allowed_shifts,
        fixed_shift: formData.fixed_shift,
        grade: formData.grade,
        is_weekend_off: formData.is_weekend_off,
        wanted_max_requests: formData.wanted_max_requests,
        weekly_off_weekday: formData.weekly_off_weekday,
      },
      initialActiveAssignmentsRef.current,
      formData.dispatch_activities,
      formData.reason_activity,
      formatDateToString
    );
    const baseAssignmentPayloads = assignmentPatchAllowed
      ? allAssignmentPayloads
      : allAssignmentPayloads.filter((p) => p.operation === "cancel");
    // 프리셉티(본인) 관계는 assignment 가 아니라 nurse_preceptee_period 로 write.
    const precepteeSelfPeriodPayload = assignmentPatchAllowed
      ? buildPrecepteeSelfPeriodPayload(
          nurseDetailData,
          preceptorMode,
          formData.preceptor_id,
          precepteeAssignmentPeriodDraft,
          formatDateToString
        )
      : null;
    const assignmentPayloads = baseAssignmentPayloads;

    const formDataForPatch = {
      ...formData,
      exclusion_partner_id: effectiveExclusionPartnerId,
    };

    const fullProfilePatch = buildFullNurseProfilePatch({
      formData: formDataForPatch,
      teamsSchema: teamsSchema ?? [],
      preceptorMode,
      payloadResignationDate,
      payloadResignationMemo,
      omitIsWeekendOff:
        resolveDispatchTargetRow(nurseDetailData, selectedGroupId) != null,
    });

    const profileDiff = patchRecordDiff(
      initialNurseProfilePatchRef.current,
      fullProfilePatch
    );

    /** 타인 preceptor_id — 본인 patch에 안 잡힘; selectedPreceptees·모드와 DB만 바뀐 경우 유지 */
    const preceptorAssignmentPatchAllowed =
      !(onlyView ?? false) && !disableShiftAndWantedEdit;
    const hasInitialPreceptorAssignments =
      Object.keys(initialPreceptorAssignmentByNurseIdRef.current).length > 0;
    const preceptorAssignmentPayloads =
      preceptorAssignmentPatchAllowed &&
      (preceptorMode === "preceptor" || hasInitialPreceptorAssignments)
        ? buildPreceptorPeerUpdatePayloads({
            initialByNurseId: initialPreceptorAssignmentByNurseIdRef.current,
            selectedPrecepteeIds:
              preceptorMode === "preceptor" ? selectedPreceptees : [],
            draftByNurseId: preceptorAssignmentDraftByNurseId,
            defaultStartDate: defaultPreceptorAssignmentStartDate,
          })
        : [];

    const hasAnyChange =
      Object.keys(profileDiff).length > 0 ||
      assignmentPayloads.length > 0 ||
      preceptorAssignmentPayloads.length > 0 ||
      precepteeSelfPeriodPayload != null;
    if (!hasAnyChange) {
      toast.info("변경된 내용이 없습니다.");
      setIsSaveModalOpen(false);
      return;
    }

    const nursePatchBody: Record<string, unknown> = omitUndefinedValues({
      ...profileDiff,
    });

    sanitizeNursePatchBody(nursePatchBody);

    /** 명세: 단건은 `assignment`, 복수는 `assignments` — inbound 배열 통째 PUT 금지 */
    if (assignmentPayloads.length === 1) {
      nursePatchBody.assignment = assignmentPayloads[0];
    } else if (assignmentPayloads.length > 1) {
      nursePatchBody.assignments = assignmentPayloads;
    }

    if (preceptorAssignmentPayloads.length > 0) {
      nursePatchBody.preceptor_periods = preceptorAssignmentPayloads;
    }

    // 프리셉티(본인) 관계 → period 전용 필드(assignment 아님).
    if (precepteeSelfPeriodPayload != null) {
      nursePatchBody.preceptee_period = precepteeSelfPeriodPayload;
    }

    // 1. 현재 수정된 간호사 데이터 객체 생성
    const updatedCurrentNurse: NurseResponse = {
      ...nurseData!,
      ...formData,
      team_id: targetTeamId,
      grade: formData.grade === 0 ? null : formData.grade,
      experience: formData.experience === "" ? 1 : Number(formData.experience),
      // 문자열을 Date 객체로 변환
      joining_date: formData.joining_date
        ? new Date(formData.joining_date)
        : null,
      resignation_date: payloadResignationDate
        ? new Date(payloadResignationDate)
        : null,
      resignation_reason: formData.resignation_reason,
      resignation_reason_memo: payloadResignationMemo,
      allowed_shifts: formData.allowed_shifts as UnionShift[],
      // 프리셉터 모드라면 자신의 preceptor_id는 null
      preceptor_id:
        preceptorMode === "preceptor" ? null : formData.preceptor_id || null,
      exclusion_partner_id: effectiveExclusionPartnerId,
    };

    // 2. 전체 리스트 재구성 (Bulk Update용)
    const updatedNurses = allNurses.map((nurse) => {
      // 2-1. 현재 수정 중인 간호사
      if (nurse.nurse_id === nurseDetailData.nurse_id) {
        return updatedCurrentNurse;
      }

      // 2-2. 프리셉터 관계 정리
      if (preceptorMode === "preceptor") {
        // 내가 프리셉터일 때:
        // 목록에 있는 간호사는 나를 프리셉터로 지정
        if (selectedPreceptees.includes(nurse.nurse_id)) {
          return { ...nurse, preceptor_id: nurseDetailData.nurse_id };
        }
        // 목록에 없는데 원래 나를 프리셉터로 하고 있던 간호사는 해제
        if (
          nurse.preceptor_id === nurseDetailData.nurse_id &&
          !selectedPreceptees.includes(nurse.nurse_id)
        ) {
          return { ...nurse, preceptor_id: null };
        }
      } else {
        // 내가 프리셉터가 아닐 때 (프리셉티 모드이거나 아무것도 아님):
        // 원래 나를 프리셉터로 하고 있던 간호사들이 있다면 해제해야 함
        if (nurse.preceptor_id === nurseDetailData.nurse_id) {
          return { ...nurse, preceptor_id: null };
        }
      }

      return nurse;
    });
    void updatedNurses;

    const groupIdForQuery = isAdmin ? (selectedGroupId ?? "") : "";
    await saveNurseMutation.mutateAsync({
      nursePatchBody,
      nurseId: nurseDetailData.nurse_id,
      groupIdForQuery,
    });
  };

  // shiftsData는 이미 useShiftsQuery로 가져온 shiftsRes를 사용
  // processShifts가 이미 정렬과 시간 변환을 처리하므로 중복 쿼리 제거
  const shiftsData = shiftsRes as type.availableShifts[] | undefined;

  const handleSubShiftToggle = useCallback(
    (shiftId: string) => {
      setFormData((prev) => {
        const isSelected = prev.work_shifts.includes(shiftId);

        if (isSelected) {
          return {
            ...prev,
            work_shifts: prev.work_shifts.filter((id) => id !== shiftId),
          };
        }

        const targetShift = shiftsData?.find((s) => s.shift_id === shiftId);
        const targetGb = targetShift?.shift_gb;

        // 같은 그룹(shift_gb)의 다른 근무 제거 후 새 근무 추가
        const newWorkShifts = prev.work_shifts.filter((id) => {
          const s = shiftsData?.find((shift) => shift.shift_id === id);
          return !targetGb || s?.shift_gb !== targetGb;
        });

        return {
          ...prev,
          work_shifts: [...newWorkShifts, shiftId],
        };
      });
    },
    [shiftsData]
  );

  // 주말 휴무 적용 + 근무형태 고정 선택 시 서브 근무 초기화
  useEffect(() => {
    if (
      formData.is_weekend_off &&
      formData.fixed_shift !== null &&
      formData.fixed_shift !== ""
    ) {
      setFormData((prev) => ({ ...prev, allowed_shifts: [], work_shifts: [] }));
    }
  }, [formData.is_weekend_off, formData.fixed_shift]);

  // onlyView가 true일 때만 disabled 처리
  const isDisabledNurse = onlyView ?? false;
  const isStatusAndWantedLimitDisabled =
    isDisabledNurse || disableShiftAndWantedEdit;
  // 전입자(target) 뷰: 추가/수정은 막되(disabled 유지) 기존 inbound 파견/병동이동 '취소'만 허용
  const allowCancelInbound = !(onlyView ?? false) && disableShiftAndWantedEdit;
  const normalizedReasonValue =
    formData.resignation_reason === "부서이동"
      ? "병동이동"
      : formData.resignation_reason;
  const activeReason = (
    normalizedReasonValue &&
    ["휴직", "퇴사", "파견", "병동이동"].includes(normalizedReasonValue)
      ? normalizedReasonValue
      : null
  ) as SupportedReason | null;
  const activeReasonActivity =
    activeReason === "파견"
      ? (formData.dispatch_activities[0] ?? null)
      : activeReason != null
        ? formData.reason_activity[activeReason]
        : null;
  const minSelectableDateForStatus = useMemo(() => {
    // 현재 근무자 관리 편집 규칙: 오늘은 선택 불가, 내일부터 선택 가능
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = formatDateToString(tomorrow);
    if (!formData.joining_date) return tomorrowStr;
    return formData.joining_date > tomorrowStr
      ? formData.joining_date
      : tomorrowStr;
  }, [formData.joining_date, formatDateToString]);
  if (!isOpen) return null;
  const occupiedRangesForActiveReason = (() => {
    const ranges: Array<{ startDate: string; endDate: string }> = [];

    if (activeReason !== "파견") {
      for (const row of formData.dispatch_activities) {
        if (row.startDate && row.endDate) {
          ranges.push({ startDate: row.startDate, endDate: row.endDate });
        }
      }
    }

    if (activeReason !== "휴직") {
      const leave = formData.reason_activity["휴직"];
      if (leave.startDate && leave.endDate) {
        ranges.push({ startDate: leave.startDate, endDate: leave.endDate });
      }
    }

    if (activeReason !== "병동이동") {
      const transfer = formData.reason_activity["병동이동"];
      if (transfer.startDate) {
        ranges.push({
          startDate: transfer.startDate,
          endDate: transfer.startDate,
        });
      }
    }

    if (activeReason !== "퇴사") {
      const retire = formData.reason_activity["퇴사"];
      if (retire.endDate) {
        ranges.push({ startDate: retire.endDate, endDate: retire.endDate });
      }
    }

    return ranges;
  })();

  const sidebarContent = (
    <>
      {/* 오버레이: main 스크롤과 무관하게 뷰포트 전체를 덮음 (absolute는 스크롤에 같이 밀림) */}
      <div
        className="fixed inset-0 z-40 bg-black/0 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 사이드바 */}
      <div
        className={`fixed flex flex-col  top-[78px] border-t-12 border-sig bg-zinc-100 rounded-l-lg overflow-y-auto scrollbar-float bottom-[12px] right-0 h-[calc(100vh-90px)] mr-[12px] w-[450px] shadow-xl z-50 transform transition-transform duration-300 ease-in-out rounded-bl-[10px] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {isNurseDetailLoading ? (
          <div className="flex items-center justify-center h-full min-h-[calc(100vh-90px)] bg-white">
            <LoadingSpiner
              msg={`${nurseNameFromList ? `${nurseNameFromList} ` : ""}간호사 정보를 불러오는 중입니다`}
            />
          </div>
        ) : (
          <>
            <section className="bg-white w-full flex flex-col py-5 px-6 sticky top-0 z-10 border-b-10 border-[#F0F0F2]">
              {/* 닫기 버튼 */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
              >
                <img src={images.icon_close_gray} alt="" aria-hidden="true" />
              </button>

              <div className="flex items-center gap-6 ">
                {/* 이름 */}
                <div className="flex items-center">
                  <h2 className="text-2xl font-semibold text-black font-pretendard mr-[8px]">
                    {nurseDetailData?.name || "-"}
                  </h2>
                  <span className="font-pretendard text-base text-zinc-400 font-semibold">
                    {nurseDetailData?.role || "-"}
                  </span>
                </div>

                <InfoItem
                  label="사번"
                  value={String(nurseDetailData?.nurse_id) || "-"}
                />
              </div>

              <div className="grid grid-cols-[150px_2fr] gap-x-3 pt-2 gap-y-1">
                <InfoItem
                  label="연락처"
                  value={nurseDetailData?.phone_number || "-"}
                />
                <InfoItem
                  label="재직기간"
                  value={
                    !nurseDetailData?.joining_date
                      ? "-"
                      : DateUtils.getTenureString(
                          new Date(nurseDetailData.joining_date),
                          nurseDetailData.resignation_date
                            ? new Date(nurseDetailData.resignation_date)
                            : null
                        )
                  }
                />
                <InfoItem
                  label="입사일"
                  value={
                    nurseDetailData?.joining_date
                      ? formatDateToString(
                          new Date(nurseDetailData.joining_date)
                        )
                      : "-"
                  }
                />
                {nurseDetailData?.resignation_date && (
                  <InfoItem
                    label="퇴사일"
                    value={formatDateToString(
                      new Date(nurseDetailData.resignation_date)
                    )}
                  />
                )}
              </div>
              {disableShiftAndWantedEdit && (
                <p className="text-xs mt-3 text-sig">
                  *파견 근무자는 일부 정보가 수정이 불가능합니다.
                </p>
              )}
            </section>
          </>
        )}

        {!isNurseDetailLoading && (
          <div className="flex flex-col gap-3">
            <section className="bg-white py-5 px-6 flex flex-col gap-6 ">
              <div className="grid grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-4">
                {/* Team */}
                <div className="min-w-0">
                  <div className="flex items-center mb-[7px]">
                    <label className="text-[15px] font-semibold text-text-light-gray font-pretendard">
                      Team
                    </label>
                    <span className="ml-2 truncate text-[11px] text-text-light-gray font-pretendard font-light">
                      *배정된 팀
                    </span>
                  </div>

                  <Select
                    value={teamSelectValue}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        team: value === "none" ? "" : value,
                      })
                    }
                    disabled={isDisabledNurse}
                  >
                    <SelectTrigger className="w-full h-[35px] min-w-0 border-[#D6D6DE] rounded-[5px] text-[15px] font-pretendard">
                      <SelectValue placeholder="없음" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">없음</SelectItem>
                      {sortTeamsByName(teamsSchema ?? []).map((team) => (
                        <SelectItem
                          key={team.team_id}
                          value={String(team.team_id)}
                        >
                          {team.team_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Grade */}
                <div className="min-w-0">
                  <label className="block text-[15px] font-semibold text-[#939393] mb-[7px] font-pretendard">
                    Grade
                  </label>
                  <Select
                    value={String(formData.grade)}
                    onValueChange={(value) =>
                      setFormData({ ...formData, grade: Number(value) })
                    }
                    disabled={isDisabledNurse ?? false}
                  >
                    <SelectTrigger
                      className={cn(
                        "!w-full h-[35px] min-w-0 max-w-full overflow-hidden border-[#D6D6DE] rounded-[5px] text-[15px] font-pretendard",
                        "[&_[data-slot=select-value]]:!block [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:w-full",
                        "[&_[data-slot=select-value]]:overflow-hidden [&_[data-slot=select-value]]:text-ellipsis",
                        "[&_[data-slot=select-value]]:whitespace-nowrap"
                      )}
                    >
                      <SelectValue placeholder="선택" />
                    </SelectTrigger>
                    <SelectContent
                      className="min-w-0 max-w-[min(100vw-1.5rem,20rem)] w-[var(--radix-select-trigger-width)]"
                      position="popper"
                      sideOffset={4}
                      align="start"
                    >
                      <SelectItem value="0">-</SelectItem>
                      {gradeSelectItems.map((item) => (
                        <SelectItem key={item.key} value={item.value}>
                          <span className="truncate max-w-[72px]! overflow-hidden!">
                            {item.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* 경력 */}
                <div
                  className={cn(
                    "min-w-0",
                    isStatusAndWantedLimitDisabled
                      ? "opacity-55 transition-opacity"
                      : ""
                  )}
                >
                  <label className="block text-[15px] font-semibold text-[#939393] mb-[7px] font-pretendard">
                    경력
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.experience}
                      onChange={(e) => {
                        // 숫자만 입력 가능
                        const value = e.target.value.replace(/\D/g, "");
                        // 빈 값이거나 1 이상인 경우만 허용
                        if (value === "" || parseInt(value, 10) >= 1) {
                          setFormData({ ...formData, experience: value });
                        }
                      }}
                      onBlur={() => {
                        // blur 시 빈 값이면 1로 설정
                        if (
                          formData.experience === "" ||
                          parseInt(formData.experience, 10) < 1
                        ) {
                          setFormData({ ...formData, experience: "1" });
                        }
                      }}
                      placeholder="1"
                      disabled={isStatusAndWantedLimitDisabled}
                      className="w-full h-[35px] px-[15px] pr-[30px] border border-[#D6D6DE] rounded-[5px] text-[15px] !font-pretendard outline-none text-[#333333] disabled:cursor-not-allowed"
                    />
                    <span className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[15px] font-pretendard pointer-events-none text-[#333333]">
                      년
                    </span>
                  </div>
                </div>
                {/* 주휴 영역 */}
                <div className="min-w-0">
                  <NurseMyWeeklyOffSection
                    weeklyOffWeekday={formData.weekly_off_weekday}
                    initialWeeklyOffWeekday={
                      effectiveNurseDetail?.weekly_off_weekday ?? null
                    }
                    onWeeklyOffChange={(value) =>
                      setFormData({ ...formData, weekly_off_weekday: value })
                    }
                    cycleStartDate={formatDateToString(
                      weeklyOffSettingsData?.cycle_start_date
                    )}
                    disabled={isDisabledNurse}
                  />
                </div>

                <div className="col-span-2 min-w-0">
                  <label className="block text-[15px] font-semibold text-[#939393] mb-[7px] font-pretendard">
                    직급
                  </label>
                  <Input
                    type="text"
                    value={formData.level_}
                    onChange={(event) =>
                      setFormData({ ...formData, level_: event.target.value })
                    }
                    disabled={isDisabledNurse}
                    placeholder="직급 입력"
                    className="w-full h-[35px] min-w-0 border-[#D6D6DE] rounded-[5px] text-[15px] font-pretendard"
                  />
                </div>
              </div>

              {/* 프리셉터 */}
              <div className="flex flex-col gap-1">
                {/* 프리셉터 섹션 컴포넌트 */}
                <NursePreceptorSection
                  preceptorMode={preceptorMode}
                  selectedPreceptees={selectedPreceptees}
                  preceptorId={formData.preceptor_id}
                  availablePreceptees={precepteeOptionsForSection}
                  preceptorList={preceptorList}
                  nurseId={nurseDetailData?.nurse_id}
                  precepteeAssignmentPeriod={precepteeAssignmentPeriodDraft}
                  preceptorAssignmentPeriods={preceptorAssignmentDraftByNurseId}
                  isPrecepteeStartDateError={isPrecepteeStartDateError}
                  onModeChange={handlePreceptorModeChange}
                  onPrecepteesChange={handlePrecepteesChange}
                  onPreceptorChange={(value) => {
                    setFormData({ ...formData, preceptor_id: value });
                    if (!value) {
                      setIsPrecepteeStartDateError(false);
                    }
                  }}
                  onPrecepteeAssignmentPeriodChange={(value) => {
                    setPrecepteeAssignmentPeriodDraft(value);
                    if (value.startDate) {
                      setIsPrecepteeStartDateError(false);
                    }
                  }}
                  onPreceptorAssignmentPeriodChange={
                    handlePreceptorAssignmentPeriodChange
                  }
                  disabled={isStatusAndWantedLimitDisabled}
                />
              </div>

            </section>

            <section className="bg-white py-5 px-6 border-t border-[#F0F1F4]">
              <div className="mb-3">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-[15px] font-semibold text-[#333333] font-pretendard">
                    상호 근무 배제
                  </h3>
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex size-4 cursor-help items-center justify-center rounded-full border border-[#CDD5E0] text-[11px] font-semibold leading-none text-[#7B8190]">
                          ?
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="w-max px-3 py-2">
                        <p className="whitespace-nowrap text-xs font-normal leading-5">
                          프리셉터/프리셉티 관계자는
                          <br />
                          상호 배제 리스트에서 제외됩니다.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="mt-1 text-[12px] leading-5 text-[#7B8190] font-pretendard">
                  선택한 근무자와 같은 날 같은 근무조 배정을 피하도록 설정합니다.
                </p>
                {isExclusionPartnerBlocked && (
                  <p className="mt-1 text-[12px] leading-5 text-amber-700 font-pretendard">
                    현재 상호배제 대상이 프리셉터/프리셉티 관계에 포함되어 저장 시 해제됩니다.
                  </p>
                )}
              </div>
              <Select
                value={effectiveExclusionPartnerId ?? "none"}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    exclusion_partner_id: value === "none" ? null : value,
                  })
                }
                disabled={isStatusAndWantedLimitDisabled}
              >
                <SelectTrigger className="w-full h-[35px] min-w-0 border-[#D6D6DE] rounded-[5px] text-[15px] font-pretendard">
                  <SelectValue placeholder="지정 안함" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">지정 안함</SelectItem>
                  {exclusionPartnerOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </section>

            <section className="bg-white py-5 px-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                  근무형태 {isUseMid ? "(D,E,N,M)" : "(D,E,N)"}
                </label>
              </div>

              <NurseMonthlyLimitSection
                nurseId={nurseDetailData?.nurse_id ?? nurseId}
                groupId={selectedGroupId || nurseDetailData?.group_id || null}
                nurseName={nurseDetailData?.name || nurseNameFromList}
                teamName={currentTeam}
                selectedMainShifts={formData.allowed_shifts}
                availableShifts={shiftsData}
                isUseMid={isUseMid}
                disabled={isDisabledNurse}
              />

              {/* 근무형태 */}
              <ShiftTypeSelector
                shiftMapper={shiftMapper}
                selectedMainShifts={formData.allowed_shifts}
                onMainShiftToggle={toggleNightNurse}
                selectedSubShifts={formData.work_shifts}
                onSubShiftToggle={handleSubShiftToggle}
                availableShifts={shiftsData}
                weekendOff={formData.is_weekend_off ? 1 : 0}
                fixedShift={formData.fixed_shift}
                disabled={isDisabledNurse}
              />

              <NurseShiftInfoSection
                isWeekendOff={formData.is_weekend_off}
                onWeekendOffChange={handleWeekendOffChange}
                fixedShift={formData.fixed_shift}
                onFixedShiftChange={handleFixedShiftChange}
                availableShifts={shiftsData}
                disabled={isDisabledNurse}
              />
            </section>

            <section className="bg-white py-5 px-6">
              {/* 근무종료일 및 휴직, 퇴사, 파견, 병동이동 */}
              <NurseDateInfoSection
                joiningDate={formData.joining_date}
                minSelectableDateForStatus={minSelectableDateForStatus}
                startDate={activeReasonActivity?.startDate ?? ""}
                resignationDate={activeReasonActivity?.endDate ?? ""}
                targetGroupId={activeReasonActivity?.targetGroupId ?? ""}
                onStartDateChange={(value) => {
                  if (activeReason == null) return;
                  setReasonValidationErrors((prev) => ({
                    ...prev,
                    startDate: false,
                  }));
                  setFormData((prev) => ({
                    ...prev,
                    reason_activity: {
                      ...prev.reason_activity,
                      [activeReason]: {
                        ...prev.reason_activity[activeReason],
                        startDate: value,
                      },
                    },
                  }));
                }}
                onTargetGroupIdChange={(value) => {
                  if (activeReason == null) return;
                  setReasonValidationErrors((prev) => ({
                    ...prev,
                    targetGroupId: false,
                  }));
                  setFormData((prev) => ({
                    ...prev,
                    reason_activity: {
                      ...prev.reason_activity,
                      [activeReason]: {
                        ...prev.reason_activity[activeReason],
                        targetGroupId: value,
                      },
                    },
                  }));
                }}
                onJoiningDateChange={(value) =>
                  setFormData({ ...formData, joining_date: value })
                }
                onResignationDateChange={(value) => {
                  if (activeReason == null) return;
                  setReasonValidationErrors((prev) => ({
                    ...prev,
                    endDate: false,
                  }));
                  setFormData((prev) => ({
                    ...prev,
                    reason_activity: {
                      ...prev.reason_activity,
                      [activeReason]: {
                        ...prev.reason_activity[activeReason],
                        endDate: value,
                      },
                    },
                  }));
                }}
                resignationReason={
                  formData.resignation_reason as
                    | "휴직"
                    | "퇴사"
                    | "파견"
                    | "병동이동"
                    | null
                    | undefined
                }
                resignationReasonMemo={activeReasonActivity?.memo ?? null}
                onResignationReasonChange={(value) => {
                  setReasonValidationErrors({
                    startDate: false,
                    endDate: false,
                    targetGroupId: false,
                  });
                  setFormData({
                    ...formData,
                    resignation_reason: value,
                  });
                }}
                onResignationReasonMemoChange={(value) => {
                  if (activeReason == null) return;
                  setFormData((prev) => ({
                    ...prev,
                    reason_activity: {
                      ...prev.reason_activity,
                      [activeReason]: {
                        ...prev.reason_activity[activeReason],
                        memo: value,
                      },
                    },
                  }));
                }}
                validationErrors={
                  activeReason != null ? reasonValidationErrors : undefined
                }
                excludeTargetGroupId={nurseDetailData?.group_id ?? null}
                disabled={isStatusAndWantedLimitDisabled}
                allowCancel={allowCancelInbound}
                onImmediateCancel={handleImmediateCancelAssignment}
                isImmediateCancelPending={immediateCancelMutation.isPending}
                reasonHasInboundData={reasonHasInboundData}
                occupiedRanges={occupiedRangesForActiveReason}
                onResetSingleReason={(reason) => {
                  // 병동이동: 저장된 배정이면 저장하기 없이 즉시 취소(cancel)
                  if (reason === "병동이동") {
                    const existing = initialActiveAssignmentsRef.current.find(
                      (a) => normalizeAssignmentReason(a.reason) === "병동이동"
                    );
                    const aid =
                      typeof existing?.id === "number" ? existing.id : null;
                    if (aid != null) {
                      handleImmediateCancelAssignment(aid);
                      return;
                    }
                  }
                  setReasonValidationErrors({
                    startDate: false,
                    endDate: false,
                    targetGroupId: false,
                  });
                  setFormData((prev) => ({
                    ...prev,
                    reason_activity: {
                      ...prev.reason_activity,
                      [reason]: {
                        startDate: "",
                        endDate: "",
                        memo: null,
                        targetGroupId: "",
                      },
                    },
                  }));
                }}
                useOfficeGroupsForTarget={fetchGroupsByOfficeEnabled}
                officeIdForTargetGroups={officeIdForByOffice}
                dispatchActivities={formData.dispatch_activities}
                onImmediateDispatchSave={handleImmediateDispatchSave}
                isImmediateDispatchSaving={immediateDispatchMutation.isPending}
                onDispatchActivitiesChange={(next) => {
                  setReasonValidationErrors({
                    startDate: false,
                    endDate: false,
                    targetGroupId: false,
                  });
                  setFormData((prev) => ({
                    ...prev,
                    dispatch_activities: next,
                    reason_activity: {
                      ...prev.reason_activity,
                      파견:
                        next[0] != null
                          ? {
                              startDate: next[0].startDate,
                              endDate: next[0].endDate,
                              memo: next[0].memo,
                              targetGroupId: next[0].targetGroupId,
                            }
                          : {
                              startDate: "",
                              endDate: "",
                              memo: null,
                              targetGroupId: "",
                            },
                    },
                  }));
                }}
              />
              {activeReason === "파견" && nurseDetailData && (
                <DispatchHistory
                  nurseId={nurseDetailData.nurse_id}
                  groupId={nurseDetailData.group_id}
                  officeId={nurseDetailData.office_id}
                  accountId={loginUser?.account_id ?? ""}
                />
              )}
            </section>

            {/* 원티드 설정 영역 */}
            <section className="bg-white py-5 px-6 grid grid-cols-[160px_1fr] gap-x-5">
              <div className="flex flex-col row-span-2 gap-2 justify-between border-r-3 pr-5 border-zinc-100">
                <label className="text-base font-semibold text-[#939393] font-pretendard">
                  신청 가능한 휴가·휴무
                </label>
                <Input
                  type="number"
                  min="0"
                  max="99"
                  value={formData.wanted_max_requests ?? ""}
                  placeholder="-"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      wanted_max_requests:
                        e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  disabled={isStatusAndWantedLimitDisabled}
                  className={` border border-[#D6D6DE] rounded-[5px] text-base font-pretendard disabled:cursor-not-allowed ${
                    isStatusAndWantedLimitDisabled ? "opacity-55" : ""
                  }`}
                />
              </div>

              <div className="flex items-center justify-between mb-2">
                <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                  원티드 AI 기능
                </label>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[12px] font-pretendard transition-colors ${
                      formData.enable_aide ? "text-[#0E59C6]" : "text-[#D6D6DE]"
                    }`}
                  >
                    {formData.enable_aide ? "사용" : "사용안함"}
                  </span>
                  <label
                    className={`relative inline-flex items-center cursor-pointer ${
                      isStatusAndWantedLimitDisabled ? "opacity-55" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.enable_aide}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setFormData({
                          ...formData,
                          enable_aide: isChecked,
                          ...(!isChecked && {
                            enable_nurse_pair_preference: false,
                          }),
                        });
                      }}
                      disabled={isStatusAndWantedLimitDisabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E59C6]"></div>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                  원티드 시크릿 기능
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    <span
                      className={`text-[12px] font-pretendard transition-colors ${
                        formData.enable_nurse_pair_preference
                          ? "text-[#0E59C6]"
                          : "text-[#D6D6DE]"
                      }`}
                    >
                      {formData.enable_nurse_pair_preference
                        ? "사용"
                        : "사용안함"}
                    </span>
                  </div>

                  <label
                    className={`relative inline-flex items-center ${
                      isStatusAndWantedLimitDisabled || !formData.enable_aide
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    } ${isStatusAndWantedLimitDisabled ? "opacity-55" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.enable_nurse_pair_preference}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          enable_nurse_pair_preference: e.target.checked,
                        })
                      }
                      disabled={
                        isStatusAndWantedLimitDisabled || !formData.enable_aide
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E59C6]"></div>
                  </label>
                </div>
              </div>
            </section>

            <section className="bg-white py-5 px-6">
              <div className="mb-5">
                <div className="flex items-center mb-[7px]">
                  <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                    메모
                  </label>
                </div>

                <input
                  value={formData.nurse_memo}
                  onChange={(e) =>
                    setFormData({ ...formData, nurse_memo: e.target.value })
                  }
                  disabled={isStatusAndWantedLimitDisabled}
                  className="w-full px-[15px] py-2 border border-[#D6D6DE] rounded-[5px] text-[15px] font-pretendard h-[35px] disabled:cursor-not-allowed"
                />
              </div>

              {/* 관리자 권한 */}
              <div className="mb-[15px]">
                <div className="flex items-center justify-between">
                  <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                    관리자 권한
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_head_nurse}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_head_nurse: e.target.checked,
                        })
                      }
                      disabled={isDisabledNurse}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E59C6]"></div>
                  </label>
                </div>
              </div>

              {isAdmin && (
                <>
                  {/* 그룹 관리자 권한 */}
                  <div className="mb-[15px]">
                    <div className="flex items-center justify-between">
                      <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                        그룹 관리자 권한
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_group_admin}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            // false로 변경하려고 할 때만 모달 표시
                            if (!isChecked && formData.is_group_admin) {
                              setIsGroupAdminChangeModalOpen(true);
                            } else {
                              // true로 변경하는 경우는 바로 적용
                              setFormData({
                                ...formData,
                                is_group_admin: isChecked,
                                ...(!isChecked && { managed_group_ids: [] }),
                              });
                            }
                          }}
                          disabled={isDisabledNurse}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E59C6]"></div>
                      </label>
                    </div>
                  </div>

                  {/* 관리 그룹 */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                        관리 그룹
                      </label>
                    </div>

                    <GroupMultiSelect
                      label="관리 그룹"
                      selectedGroupIds={formData.managed_group_ids}
                      onChange={(groupIds) =>
                        setFormData((prev) => ({
                          ...prev,
                          managed_group_ids: groupIds,
                        }))
                      }
                      groups={groups || []}
                      disabled={isDisabledNurse || !formData.is_group_admin}
                    />
                  </div>
                </>
              )}

              {/* 휴가 대상 3-state — 보건휴가 · 수면OFF · 임산부.
                  ★ 다른 항목과 달리 이 값은 저장 버튼을 거치지 않고 즉시 반영된다.
                    period 테이블에 직접 쓰는 별도 엔드포인트라 formData 와 섞이지 않는다. */}
              {nurseId && (selectedGroupId || nurseDetailData?.group_id) ? (
                <div className="flex flex-col gap-2">
                  <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
                    휴가 대상 설정
                  </label>
                  <LeaveEligibilityFields
                    nurseId={String(nurseId)}
                    groupId={String(selectedGroupId || nurseDetailData?.group_id || "")}
                    year={year ?? new Date().getFullYear()}
                    month={month ?? new Date().getMonth() + 1}
                    disabled={isDisabledNurse || onlyView}
                  />
                  {/* 수면OFF 주기 상태(읽기 전용). 위 설정이 "대상인가" 라면
                      이건 "지금 어디까지 왔나" 다 — 근무표에 N 연번이 없어
                      여기서만 확인된다. */}
                  <NightCycleStatus
                    nurseId={String(nurseId)}
                    groupId={String(selectedGroupId || nurseDetailData?.group_id || "")}
                    year={year ?? new Date().getFullYear()}
                    month={month ?? new Date().getMonth() + 1}
                  />
                </div>
              ) : null}
            </section>

            {!onlyView && (
              <div className="sticky bottom-0 py-4 px-6 bg-white z-10 border-t border-[#F0F0F2]">
                {/* 상단 그라데이션 */}
                <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-transparent to-white pointer-events-none -translate-y-full"></div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => throttle(handleSaveClick)}
                        disabled={
                          isDisabledNurse ||
                          saveNurseMutation.isPending ||
                          immediateDispatchMutation.isPending
                        }
                        className="bg-[#0E59C6] rounded-full py-[13px] px-[34px] transition-colors min-h-[45px] min-w-[120px] flex justify-center items-center disabled:cursor-not-allowed"
                      >
                        <span className="text-white font-pretendard font-semibold">
                          저장하기
                        </span>
                      </button>
                    </TooltipTrigger>
                    {isDisabledNurse && (
                      <TooltipContent>
                        <p>파트장 정보는 수정할 수 없습니다</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 저장 컨펌 팝업 */}
      <CustomModal.Confirm
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onConfirm={executeSave}
        isPending={saveNurseMutation.isPending}
        title="프로필을 수정하시겠습니까?"
        pendingMsg="정보를 저장하는 중입니다..."
      />
      {/* 그룹관리자 컨펌 팝업 */}
      <CustomModal.Confirm
        isOpen={isGroupAdminChangeModalOpen}
        onClose={() => setIsGroupAdminChangeModalOpen(false)}
        onConfirm={() => {
          setFormData({
            ...formData,
            is_group_admin: false,
            managed_group_ids: [],
          });
          setIsGroupAdminChangeModalOpen(false);
        }}
        title={
          <div className="flex flex-col gap-1.5">
            <span className="font-pretendard text-[14px]">
              그룹관리자 권한을 해제할까요?
            </span>
            <span className="font-pretendard text-[14px]">
              그룹 관리자 권한을 해제하면 관리그룹이 <strong>초기화</strong>
              됩니다.
            </span>
          </div>
        }
      />
      <CustomModal.Alert
        isOpen={isNightWeekendBlockedModalOpen}
        onClose={() => setIsNightWeekendBlockeModalOpen(false)}
        title="나이트 전담은 주말 휴무를 적용할 수 없습니다."
      />
    </>
  );

  // document.body에 직접 렌더링하여 main의 스크롤 영향 받지 않도록
  // return createPortal(sidebarContent, document.body);
  // main 태그가 있으면 main에, 없으면 body에 렌더링 (ConfirmModal과의 레이어 순서 문제 해결)
  const portalRoot = document.querySelector("main");
  return portalRoot
    ? createPortal(sidebarContent, portalRoot)
    : createPortal(sidebarContent, document.body);
}
